import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Shield, 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  User, 
  Mail, 
  Phone,
  Globe,
  Fingerprint,
  IdCard,
  Clock,
  Eye,
  Ban,
  Check,
  PauseCircle,
  RefreshCw,
  Search,
  Filter,
  Download,
  FileText,
  LogOut,
  Megaphone,
  Copy,
  ClipboardCheck,
  StickyNote,
  Trash2,
  Gavel,
  Save,
  X,
  Edit3,
  RotateCcw,
  Loader2,
  EyeOff,
  ShieldAlert,
  Link as LinkIcon,
} from 'lucide-react';
import { authFetch, authDownload, clearAdminToken } from '../utils/adminAuth';
import AdminShell, { StatCard } from '../components/admin/AdminShell';
import RejectReasonModal from '../components/admin/RejectReasonModal';
import { PRIMARY_SOURCES } from '../config/trafficSources';



const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Tiny inline button that copies a string to the clipboard and flips to a
 * green check for ~1.4s as visual confirmation. Falls back to a hidden
 * textarea + execCommand for browsers/contexts where the async clipboard
 * API is blocked (e.g., insecure origins, some embedded iframes).
 */
const CopyButton = ({ value, label = "value", testid }) => {
  const [copied, setCopied] = useState(false);
  if (!value || value === "N/A" || value === "Not provided") return null;

  const handleCopy = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    const text = String(value);
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch (err) {
      console.error("Copy failed", err);
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      data-testid={testid || `copy-${label}`}
      title={copied ? "Copied!" : `Copy ${label}`}
      className={`inline-flex items-center justify-center w-6 h-6 rounded-md border transition-all ml-1.5 align-middle ${
        copied
          ? "bg-emerald-50 border-emerald-200 text-emerald-600"
          : "bg-white border-slate-200 text-slate-400 hover:text-slate-700 hover:border-slate-300"
      }`}
    >
      {copied ? <ClipboardCheck className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
    </button>
  );
};

/**
 * Inline card shown right under a "Yes" duplicate flag — surfaces *which*
 * prior publisher matched, so admin can investigate the link in one click.
 *  - kind: human label (IP / Fingerprint / ID)
 *  - match: { id, full_name, email, country, status, submitted_at }
 */
const DuplicateMatchCard = ({ kind, match }) => {
  if (!match) return null;
  const submitted = match.submitted_at
    ? new Date(match.submitted_at).toLocaleDateString("en-US", {
        year: "numeric", month: "short", day: "numeric",
      })
    : "—";
  const statusColor = {
    approved: "text-emerald-700 bg-emerald-50 border-emerald-200",
    pending_review: "text-amber-700 bg-amber-50 border-amber-200",
    blocked: "text-red-700 bg-red-50 border-red-200",
    on_hold: "text-amber-700 bg-amber-50 border-amber-200",
  }[match.status] || "text-slate-700 bg-slate-50 border-slate-200";

  return (
    <div
      data-testid={`duplicate-match-${kind.toLowerCase()}`}
      className="ml-6 mt-1 mb-2 rounded-lg border border-amber-200 bg-amber-50/60 p-2.5 text-xs"
    >
      <div className="flex items-center gap-1.5 text-amber-800 font-semibold mb-1.5">
        <AlertTriangle className="w-3 h-3" />
        Matches prior applicant — by {kind}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-slate-700">
        <p>
          <span className="text-slate-400">Name:</span>{" "}
          <span className="font-medium text-slate-900">{match.full_name || "—"}</span>
          <CopyButton value={match.full_name} label="prior name" testid={`copy-prior-name-${kind}`} />
        </p>
        <p>
          <span className="text-slate-400">Email:</span>{" "}
          <span className="font-mono">{match.email || "—"}</span>
          <CopyButton value={match.email} label="prior email" testid={`copy-prior-email-${kind}`} />
        </p>
        <p>
          <span className="text-slate-400">Country:</span> {match.country || "—"}
        </p>
        <p>
          <span className="text-slate-400">Submitted:</span> {submitted}
        </p>
        <p className="col-span-full flex items-center gap-2 mt-0.5">
          <span className="text-slate-400">Status:</span>
          <span
            className={`inline-block px-1.5 py-0.5 rounded border text-[10px] font-semibold uppercase tracking-wider ${statusColor}`}
          >
            {match.status || "—"}
          </span>
          <span className="text-slate-400">·</span>
          <span className="text-slate-400">Prior ID:</span>
          <code className="text-[10px] text-slate-600">{match.id || "—"}</code>
          <CopyButton value={match.id} label="prior id" testid={`copy-prior-id-${kind}`} />
        </p>
      </div>
    </div>
  );
};

const AdminPublishersPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [publishers, setPublishers] = useState([]);

  // Compact date format used in the table (e.g. "Apr 24") and tooltip.
  const formatDateShort = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return null;
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return null; }
  };
  const formatDateTime = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return null;
      return d.toLocaleString();
    } catch { return null; }
  };
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPublisher, setSelectedPublisher] = useState(null);
  // Tracks the blacklist confirmation state INSIDE the KYC modal so admins
  // get a persistent visual "✓ Added to blacklist" instead of a fleeting alert.
  // Reset whenever a new publisher is opened.
  const [blacklistState, setBlacklistState] = useState({ status: 'idle', reason: '' });
  const [idImageUrl, setIdImageUrl] = useState(null);
  const [idImageLoading, setIdImageLoading] = useState(false);
  const [idImageError, setIdImageError] = useState(null);
  const [idImageReloadKey, setIdImageReloadKey] = useState(0);
  const [notesDraft, setNotesDraft] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesSavedAt, setNotesSavedAt] = useState(null);
  // Offer requests tied to the currently opened publisher.
  const [offerRequests, setOfferRequests] = useState([]);
  const [offerRequestsLoading, setOfferRequestsLoading] = useState(false);
  const [applyLinkCopied, setApplyLinkCopied] = useState(false);
  const [reviewingReqId, setReviewingReqId] = useState(null);
  // Structured rejection modal — used for both publisher disapprove and
  // offer-request reject. `context` decides which endpoint to hit on confirm.
  const [rejectModal, setRejectModal] = useState(null);
  const [terminating, setTerminating] = useState(false);
  const [closing, setClosing] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkRunning, setBulkRunning] = useState(false);
  // PDF viewer state: { appId, type, blobUrl, loading, error, publisherName }
  const [pdfPreview, setPdfPreview] = useState(null);
  // Privacy toggle — when true, mask publisher names & emails in the table
  // for screensharing / demos. Detail modal still shows the real values.
  const [hidePII, setHidePII] = useState(false);

  // Mask helpers used by the table rows when hidePII is on. We keep first &
  // last initials so admins can still eyeball a specific row.
  const maskName = (name) => {
    if (!hidePII || !name) return name;
    const parts = String(name).trim().split(/\s+/);
    return parts.map((p) => (p[0] || '').toUpperCase() + '••••').join(' ');
  };
  const maskEmail = (email) => {
    if (!hidePII || !email) return email;
    const [local, domain] = String(email).split('@');
    if (!domain) return '••••••';
    const head = (local || '').slice(0, 2);
    return `${head}••••@${domain.replace(/^[^.]+/, '••••')}`;
  };

  const fetchPublishers = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`/api/publisher/applications`);
      if (!response.ok) return; // 401 handler already redirects
      const data = await response.json();
      setPublishers(data);
    } catch (error) {
      console.error('Error fetching publishers:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPublishers();
  }, []);

  // Auto-open the publisher detail modal when the URL carries `?highlight=<id>`
  // — used by the Global Offer Requests inbox's "Open" action so admins land
  // straight on the publisher's card. Once opened we clear the query param
  // so a refresh doesn't re-trigger it.
  useEffect(() => {
    const highlight = searchParams.get('highlight');
    if (!highlight || publishers.length === 0) return;
    const match = publishers.find((p) => p.id === highlight);
    if (match) {
      setSelectedPublisher(match);
      const next = new URLSearchParams(searchParams);
      next.delete('highlight');
      setSearchParams(next, { replace: true });
    }
  }, [publishers, searchParams, setSearchParams]);

  // Load the protected ID image as a blob when a publisher is selected.
  // Works for BOTH storage backends: legacy local disk (id_file_path) and
  // new Cloudinary uploads (id_cloudinary_public_id). The backend endpoint
  // auto-detects which one the record uses and streams the bytes back.
  useEffect(() => {
    let revokeUrl = null;
    let cancelled = false;
    const hasAnyId = selectedPublisher?.id_file_path || selectedPublisher?.id_cloudinary_public_id;
    if (!selectedPublisher || !hasAnyId) {
      setIdImageUrl(null);
      setIdImageError(null);
      return;
    }
    setIdImageLoading(true);
    setIdImageError(null);
    (async () => {
      try {
        const resp = await authFetch(`/api/publisher/${selectedPublisher.id}/id-image`);
        if (!resp.ok) {
          let serverMsg = '';
          try {
            const j = await resp.json();
            serverMsg = j?.detail || '';
          } catch { /* not json */ }
          if (!cancelled) {
            setIdImageUrl(null);
            setIdImageError({ status: resp.status, message: serverMsg });
          }
          return;
        }
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        revokeUrl = url;
        if (!cancelled) {
          setIdImageUrl(url);
          setIdImageError(null);
        }
      } catch (e) {
        if (!cancelled) {
          setIdImageUrl(null);
          setIdImageError({ status: 0, message: 'Network error' });
        }
      } finally {
        if (!cancelled) setIdImageLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (revokeUrl) URL.revokeObjectURL(revokeUrl);
    };
  }, [selectedPublisher, idImageReloadKey]);

  // ─── Load proof-of-work images as authenticated blob URLs ─────────
  const [proofBlobUrls, setProofBlobUrls] = useState([]);
  useEffect(() => {
    const created = [];
    let cancelled = false;
    const urls = selectedPublisher?.proof_urls || [];
    if (!selectedPublisher || urls.length === 0) {
      setProofBlobUrls([]);
      return;
    }
    (async () => {
      const out = new Array(urls.length).fill(null);
      await Promise.all(urls.map(async (u, i) => {
        // If it's already a full https URL (Cloudinary secure_url), use directly
        if (typeof u === 'string' && /^https?:\/\//i.test(u)) {
          out[i] = u;
          return;
        }
        try {
          const r = await authFetch(`/api/publisher/${selectedPublisher.id}/proof/${i}`);
          if (!r.ok) return;
          const blob = await r.blob();
          const url = URL.createObjectURL(blob);
          created.push(url);
          out[i] = url;
        } catch { /* ignore per-file failures */ }
      }));
      if (!cancelled) setProofBlobUrls(out);
    })();
    return () => {
      cancelled = true;
      created.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [selectedPublisher]);

  // Sync the notes textarea + saved indicator whenever a different
  // publisher is opened in the detail modal.
  useEffect(() => {
    setNotesDraft(selectedPublisher?.admin_notes || '');
    setNotesSavedAt(null);
    // Also reset the blacklist confirmation banner between publishers so
    // an old success message doesn't leak into a different publisher.
    setBlacklistState({ status: 'idle', reason: '' });
  }, [selectedPublisher?.id]);

  // Load offer requests + reset copy-link banner when opening a new publisher.
  useEffect(() => {
    setApplyLinkCopied(false);
    setOfferRequests([]);
    if (!selectedPublisher?.id) return;
    let cancelled = false;
    (async () => {
      setOfferRequestsLoading(true);
      try {
        const resp = await authFetch(
          `/api/admin/publisher/${selectedPublisher.id}/offer-requests`
        );
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        if (!cancelled) setOfferRequests(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setOfferRequests([]);
      } finally {
        if (!cancelled) setOfferRequestsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedPublisher?.id]);

  const copyApplyLink = () => {
    if (!selectedPublisher?.id) return;
    const url = `${window.location.origin}/apply-new-offer/${selectedPublisher.id}`;
    navigator.clipboard?.writeText(url);
    setApplyLinkCopied(true);
    setTimeout(() => setApplyLinkCopied(false), 2000);
  };

  const approveOfferRequest = async (reqId) => {
    setReviewingReqId(reqId);
    try {
      const resp = await authFetch(`/api/admin/offer-requests/${reqId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const fresh = await resp.json();
      setOfferRequests((rs) => rs.map((r) => (r.id === reqId ? fresh : r)));
    } catch (e) {
      alert(`Approve failed: ${e.message}`);
    } finally {
      setReviewingReqId(null);
    }
  };

  const rejectOfferRequest = (reqId) => {
    const req = offerRequests.find((r) => r.id === reqId);
    setRejectModal({
      kind: "offer_request",
      reqId,
      targetName: req ? `${req.offer_name} · ${req.traffic_source_label}` : "",
      title: "Reject offer request",
      subtitle: "Pick the reason this offer application is being declined. The publisher will get a branded email explaining the decision.",
      confirmLabel: "Confirm & Notify Publisher",
    });
  };

  // One place resolves the modal confirmation for both flows so the payload
  // shape and the follow-up refresh stay consistent.
  const handleRejectConfirm = async (payload) => {
    if (!rejectModal) return;
    try {
      if (rejectModal.kind === "publisher") {
        const resp = await authFetch(`/api/publisher/${rejectModal.appId}/block`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        fetchPublishers();
      } else if (rejectModal.kind === "offer_request") {
        setReviewingReqId(rejectModal.reqId);
        const resp = await authFetch(
          `/api/admin/offer-requests/${rejectModal.reqId}/reject`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          },
        );
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const fresh = await resp.json();
        setOfferRequests((rs) => rs.map((r) => (r.id === rejectModal.reqId ? fresh : r)));
        setReviewingReqId(null);
      }
      setRejectModal(null);
    } catch (e) {
      alert(`Rejection failed: ${e.message}`);
      setReviewingReqId(null);
    }
  };

  const handleApprove = async (appId) => {
    try {
      await authFetch(`/api/publisher/${appId}/approve`, { method: 'POST' });
      fetchPublishers();
    } catch (error) {
      console.error('Error approving publisher:', error);
    }
  };

  const handleBlock = (appId) => {
    const pub = publishers.find((p) => p.id === appId) || selectedPublisher;
    setRejectModal({
      kind: "publisher",
      appId,
      targetName: pub ? `${pub.full_name || ""} · ${pub.email || ""}` : "",
      title: "Disapprove publisher",
      subtitle: "Pick the reason we can't approve this application. The publisher will get a branded email explaining the decision.",
      confirmLabel: "Confirm & Notify Publisher",
    });
  };

  const handleHold = async (appId) => {
    try {
      await authFetch(`/api/publisher/${appId}/hold`, { method: 'POST' });
      fetchPublishers();
    } catch (error) {
      console.error('Error placing publisher on hold:', error);
    }
  };

  const handleReactivate = async (appId) => {
    if (!window.confirm("Reactivate this publisher? Status will be reset to 'Pending Review' — you'll need to re-approve them.")) return;
    try {
      const resp = await authFetch(`/api/publisher/${appId}/reactivate`, { method: 'POST' });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      // Refetch so the row moves out of the closed/blocked/terminated group.
      fetchPublishers();
      setSelectedPublisher((p) => (p && p.id === appId ? { ...p, status: 'pending_review', closed_at: null, terminated_at: null } : p));
    } catch (error) {
      console.error('Error reactivating publisher:', error);
      alert('Failed to reactivate publisher.');
    }
  };

  // Edit modal state — separate from the detail modal so we don't lose the
  // detail view while editing. Draft holds the working copy; submitted on save.
  const [editingPublisher, setEditingPublisher] = useState(null);

  // Escape closes the details drawer (edit modal takes priority if both open)
  useEffect(() => {
    if (!selectedPublisher && !editingPublisher) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (editingPublisher) setEditingPublisher(null);
      else setSelectedPublisher(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedPublisher, editingPublisher]);
  const [editDraft, setEditDraft] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  const openEditModal = (pub) => {
    setEditingPublisher(pub);
    // Only carry over the fields the backend accepts. Others (id, status, ID
    // image, timestamps) remain immutable through this flow.
    setEditDraft({
      full_name: pub.full_name || '',
      email: pub.email || '',
      contact_method: pub.contact_method || '',
      messaging_platform: pub.messaging_platform || '',
      messaging_handle: pub.messaging_handle || '',
      company_name: pub.company_name || '',
      publisher_location: pub.publisher_location || '',
      country: pub.country || '',
      experience_duration: pub.experience_duration || '',
      previous_companies: pub.previous_companies || '',
      call_tracking_platform: pub.call_tracking_platform || '',
      call_type: pub.call_type || '',
      daily_call_volume: pub.daily_call_volume || '',
    });
  };

  const saveEdit = async () => {
    if (!editingPublisher) return;
    setEditSaving(true);
    try {
      const resp = await authFetch(`/api/publisher/${editingPublisher.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editDraft),
      });
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        alert(`Save failed: ${j?.detail || resp.status}`);
        return;
      }
      // Optimistic merge into both lists.
      setPublishers((rows) => rows.map((r) => (r.id === editingPublisher.id ? { ...r, ...editDraft } : r)));
      setSelectedPublisher((p) => (p && p.id === editingPublisher.id ? { ...p, ...editDraft } : p));
      setEditingPublisher(null);
    } catch (e) {
      alert(`Network error: ${e.message}`);
    } finally {
      setEditSaving(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!selectedPublisher) return;
    setNotesSaving(true);
    try {
      const resp = await authFetch(`/api/publisher/${selectedPublisher.id}/notes`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: notesDraft }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setNotesSavedAt(new Date());
      // Reflect locally so the modal stays in sync until next refetch.
      setSelectedPublisher((p) => (p ? { ...p, admin_notes: notesDraft } : p));
      setPublishers((rows) => rows.map((r) => (
        r.id === selectedPublisher.id ? { ...r, admin_notes: notesDraft } : r
      )));
    } catch (error) {
      console.error('Error saving notes:', error);
      alert('Could not save notes. Please try again.');
    } finally {
      setNotesSaving(false);
    }
  };

  const handleCloseAccount = async () => {
    if (!selectedPublisher) return;
    const confirmText = window.prompt(
      `Close ${selectedPublisher.full_name || 'this publisher'}'s account?\n\n` +
      `Use this for VOLUNTARY closures — publisher leaves on good terms.\n\n` +
      `This will:\n` +
      `  • Delete their Government ID from Cloudinary\n` +
      `  • Mark the account "account_closed"\n` +
      `  • Email them the polite "Account closed" notice\n\n` +
      `Type CLOSE to confirm.`
    );
    if (confirmText !== 'CLOSE') return;
    setClosing(true);
    try {
      const resp = await authFetch(`/api/publisher/${selectedPublisher.id}/close`, {
        method: 'POST',
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setSelectedPublisher(null);
      await fetchPublishers();
    } catch (error) {
      console.error('Error closing publisher account:', error);
      alert('Could not close the account. Please check the logs and try again.');
    } finally {
      setClosing(false);
    }
  };

  const handleTerminate = async () => {
    if (!selectedPublisher) return;
    const confirmText = window.prompt(
      `TERMINATE ${selectedPublisher.full_name || 'this publisher'}?\n\n` +
      `Use this ONLY for breach of the Master Publisher Agreement\n` +
      `(fake calls, TCPA violations, fraud, policy breach).\n\n` +
      `This will:\n` +
      `  • Delete their Government ID from Cloudinary\n` +
      `  • Mark the account "terminated"\n` +
      `  • FORFEIT all pending earnings (per Section 14)\n` +
      `  • Email them the formal "Notice of termination"\n\n` +
      `Type TERMINATE to confirm.`
    );
    if (confirmText !== 'TERMINATE') return;

    // Second prompt: capture the reason so we have an audit trail. Blank is
    // allowed (Cancel aborts the whole flow, empty string proceeds without one).
    const reason = window.prompt(
      "What is the reason for termination?\n\n" +
      "This will be saved to the admin notes for audit.\n" +
      "(Leave blank and press OK to terminate without a written reason.)"
    );
    if (reason === null) return;

    setTerminating(true);
    try {
      const resp = await authFetch(`/api/publisher/${selectedPublisher.id}/terminate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: (reason || "").trim() }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setSelectedPublisher(null);
      await fetchPublishers();
    } catch (error) {
      console.error('Error terminating publisher:', error);
      alert('Could not terminate the account. Please check the logs and try again.');
    } finally {
      setTerminating(false);
    }
  };

  // Quick row-level blacklist action — one click from the table, no modal
  // needed. Idempotent server-side, so double-clicking a blacklisted row
  // just refreshes the existing entry. Uses an optimistic-refresh pattern:
  // hit the endpoint, then refetch so the BL chip appears in the row.
  const handleQuickBlacklist = async (pub) => {
    if (!pub) return;
    if (pub.blacklisted) {
      // Already on the list — surface a lightweight tip and bail.
      alert(`${pub.full_name || 'This publisher'} is already on the blacklist.`);
      return;
    }
    const ok = window.confirm(
      `Add ${pub.full_name || 'this publisher'} to the BLACKLIST?\n\n` +
      `The last admin note (or a default label) will be saved as the reason.`,
    );
    if (!ok) return;
    try {
      const resp = await authFetch(`/api/publisher/${pub.id}/blacklist`, {
        method: 'POST',
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        alert(data?.detail || `Failed to add to blacklist (HTTP ${resp.status})`);
        return;
      }
      // Optimistically flip the flag so the BL chip appears instantly,
      // then reconcile with the server in the background.
      setPublishers((prev) =>
        prev.map((r) => (r.id === pub.id ? { ...r, blacklisted: true } : r)),
      );
      fetchPublishers();
    } catch (error) {
      console.error('Quick blacklist error:', error);
      alert('Could not add to blacklist. Please try again.');
    }
  };

  const handleAddToBlacklist = async () => {
    if (!selectedPublisher) return;
    const notesRaw = (selectedPublisher.admin_notes || '').trim();
    let previewReason = '';
    if (notesRaw) {
      const lines = notesRaw.split('\n').map((s) => s.trim()).filter(Boolean);
      if (lines.length) {
        let last = lines[lines.length - 1];
        // Strip "[timestamp] admin@..: reason" prefix for a cleaner preview.
        if (last.startsWith('[') && last.includes(']')) {
          const afterBracket = last.split(']').slice(1).join(']').trim();
          if (afterBracket.includes(':')) {
            const afterColon = afterBracket.split(':').slice(1).join(':').trim();
            if (afterColon) last = afterColon;
          } else {
            last = afterBracket;
          }
        }
        previewReason = last;
      }
    }

    const ok = window.confirm(
      `Add ${selectedPublisher.full_name || 'this publisher'} to the BLACKLIST?\n\n` +
      `They will be flagged instantly on any future application that matches\n` +
      `this email or phone.\n\n` +
      `Reason (from last admin note):\n` +
      `${previewReason ? `"${previewReason}"` : '(no notes yet — a default reason will be used)'}\n`,
    );
    if (!ok) return;

    setBlacklistState({ status: 'saving', reason: '' });
    try {
      const resp = await authFetch(`/api/publisher/${selectedPublisher.id}/blacklist`, {
        method: 'POST',
      });
      const data = await resp.json();
      if (!resp.ok) {
        setBlacklistState({
          status: 'error',
          reason: data?.detail || `Failed (HTTP ${resp.status})`,
        });
        return;
      }
      // Persistent in-modal success confirmation — visible until admin
      // navigates away, so no more "did it save?" confusion.
      setBlacklistState({
        status: 'saved',
        reason: data.reason_used || '',
      });
      // Refresh table in the background so counts + list stay accurate.
      fetchPublishers();
    } catch (error) {
      console.error('Error adding to blacklist:', error);
      setBlacklistState({
        status: 'error',
        reason: 'Network error — could not reach the server.',
      });
    }
  };

  // ---- Bulk selection helpers ----
  const toggleRowSelection = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleBulkAction = async (action) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    let verb, headline, consequences;
    if (action === 'terminate') {
      verb = 'TERMINATE';
      headline = `TERMINATE ${ids.length} publisher${ids.length === 1 ? '' : 's'} for breach of agreement?`;
      consequences = `  • Pending earnings FORFEITED (Section 14)\n  • Formal "Notice of termination" sent to each\n  • IDs purged from Cloudinary\n  • Audit trail retained\n\n`;
    } else if (action === 'delete') {
      verb = `DELETE ${ids.length}`;
      headline = `PERMANENTLY DELETE ${ids.length} record${ids.length === 1 ? '' : 's'}?`;
      consequences = `  • Rows wiped from MongoDB entirely\n  • IDs purged from Cloudinary\n  • NO email is sent\n  • NO audit trail is kept — this is gone forever\n  • Use this only for spam, duplicates, or obvious test rows.\n\n`;
    } else {
      verb = 'CLOSE';
      headline = `Close ${ids.length} publisher account${ids.length === 1 ? '' : 's'}?`;
      consequences = `  • Polite "Account closed" email sent to each\n  • IDs purged from Cloudinary\n  • Audit trail retained\n\n`;
    }
    const confirmText = window.prompt(headline + '\n\n' + consequences + `Type ${verb} to confirm.`);
    if (confirmText !== verb) return;

    setBulkRunning(true);
    try {
      const resp = await authFetch(`/api/publisher/bulk/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      const processed = data?.processed?.length ?? 0;
      const missing = data?.missing?.length ?? 0;
      setSelectedIds(new Set());
      await fetchPublishers();
      if (missing > 0) {
        alert(`${processed} processed · ${missing} not found (already removed?)`);
      }
    } catch (error) {
      console.error(`Bulk ${action} failed:`, error);
      alert(`Bulk ${action} failed. Check the logs and try again.`);
    } finally {
      setBulkRunning(false);
    }
  };

  const handleHardDelete = async () => {
    if (!selectedPublisher) return;
    const confirmText = window.prompt(
      `PERMANENTLY DELETE ${selectedPublisher.full_name || 'this publisher'}?\n\n` +
      `This wipes the row from MongoDB entirely.\n\n` +
      `  • NO email is sent\n` +
      `  • NO audit trail is kept\n` +
      `  • The Government ID is purged from Cloudinary\n` +
      `  • Use only for spam, duplicates, or test rows\n\n` +
      `Type DELETE to confirm.`
    );
    if (confirmText !== 'DELETE') return;
    setBulkRunning(true);
    try {
      const resp = await authFetch(`/api/publisher/${selectedPublisher.id}/hard`, {
        method: 'DELETE',
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setSelectedPublisher(null);
      await fetchPublishers();
    } catch (error) {
      console.error('Hard delete failed:', error);
      alert('Hard delete failed. Please check the logs and try again.');
    } finally {
      setBulkRunning(false);
    }
  };

  const handleLogout = () => {
    clearAdminToken();
    navigate('/admin/login', { replace: true });
  };

  const handleDownloadPdf = async (appId, type) => {
    try {
      const filename = type === 'admin'
        ? `Master_Agreement_Admin_${appId}.pdf`
        : `Master_Publisher_Agreement_${appId}.pdf`;
      await authDownload(`/api/publisher/${appId}/pdf/${type}`, filename);
    } catch (e) {
      console.error('Download failed', e);
    }
  };

  /**
   * Open the agreement PDF in an inline modal — never triggers a download.
   * Fetches the PDF as an authenticated blob and hands the browser a blob
   * URL that renders inside an <iframe>. Escape / Close revokes the URL so
   * we don't leak memory.
   */
  const handleViewPdf = async (appId, type, publisherName) => {
    setPdfPreview({ appId, type, blobUrl: null, loading: true, error: null, publisherName });
    try {
      const resp = await authFetch(`/api/publisher/${appId}/pdf/${type}`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const raw = await resp.blob();
      // Force `application/pdf` in case the browser mis-tags the blob and
      // wants to download instead of embed.
      const pdfBlob = new Blob([raw], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(pdfBlob);
      setPdfPreview({ appId, type, blobUrl, loading: false, error: null, publisherName });
    } catch (e) {
      console.error('PDF preview failed', e);
      setPdfPreview({ appId, type, blobUrl: null, loading: false, error: e.message, publisherName });
    }
  };

  const closePdfPreview = () => {
    if (pdfPreview?.blobUrl) {
      URL.revokeObjectURL(pdfPreview.blobUrl);
    }
    setPdfPreview(null);
  };

  // Close the PDF viewer on Escape and revoke the blob when the component
  // unmounts, so we never leak a fetched PDF in memory.
  useEffect(() => {
    if (!pdfPreview) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closePdfPreview();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pdfPreview?.blobUrl]);
  useEffect(() => () => {
    if (pdfPreview?.blobUrl) URL.revokeObjectURL(pdfPreview.blobUrl);
  }, [pdfPreview?.blobUrl]);

  const getRiskBadge = (level) => {
    const styles = {
      HIGH: 'bg-red-50 text-red-700 border-red-200',
      MEDIUM: 'bg-amber-50 text-amber-700 border-amber-200',
      LOW: 'bg-emerald-50 text-emerald-700 border-emerald-200'
    };
    return (
      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider border ${styles[level] || styles.LOW}`}>
        <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
        {level}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const styles = {
      approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      pending_review: 'bg-amber-50 text-amber-700 border-amber-200',
      on_hold: 'bg-orange-50 text-orange-700 border-orange-200',
      blocked: 'bg-red-50 text-red-700 border-red-200',
      account_closed: 'bg-slate-100 text-slate-600 border-slate-300',
      terminated: 'bg-red-100 text-red-800 border-red-300'
    };
    const icons = {
      approved: <CheckCircle className="w-3 h-3" />,
      pending_review: <Clock className="w-3 h-3" />,
      on_hold: <PauseCircle className="w-3 h-3" />,
      blocked: <Ban className="w-3 h-3" />,
      account_closed: <XCircle className="w-3 h-3" />,
      terminated: <Gavel className="w-3 h-3" />
    };
    // Human-readable label (DB status stays "blocked" for backwards
    // compatibility; only the display label changed to "Disapproved").
    const labels = {
      approved: 'approved',
      pending_review: 'pending review',
      on_hold: 'on hold',
      blocked: 'disapproved',
      account_closed: 'account closed',
      terminated: 'terminated',
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border inline-flex items-center gap-1.5 ${styles[status] || styles.pending_review}`}>
        {icons[status]}
        {labels[status] || status?.replace('_', ' ')}
      </span>
    );
  };

  const filteredPublishers = publishers.filter(pub => {
    const matchesFilter = filter === 'all' || 
      (filter === 'fraud' && pub.fraud_flag) ||
      (filter === 'high_risk' && pub.risk_level === 'HIGH') ||
      (filter === 'pending' && pub.status === 'pending_review') ||
      (filter === 'on_hold' && pub.status === 'on_hold') ||
      (filter === 'approved' && pub.status === 'approved') ||
      (filter === 'blocked' && pub.status === 'blocked') ||
      (filter === 'account_closed' && pub.status === 'account_closed') ||
      (filter === 'terminated' && pub.status === 'terminated') ||
      (filter === 'blacklisted' && pub.blacklisted);
    
    const matchesSearch = !searchTerm || 
      pub.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pub.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pub.id_type?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesFilter && matchesSearch;
  });

  // The set of IDs in the current filtered/searched view. We allow
  // selecting ANY visible row (including already-closed/terminated ones)
  // because hard-delete needs to be able to clean them up too.
  const offboardableIds = filteredPublishers.map((p) => p.id);
  const allVisibleSelected =
    offboardableIds.length > 0 && offboardableIds.every((id) => selectedIds.has(id));
  const someVisibleSelected =
    offboardableIds.some((id) => selectedIds.has(id)) && !allVisibleSelected;
  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      if (allVisibleSelected) {
        const next = new Set(prev);
        offboardableIds.forEach((id) => next.delete(id));
        return next;
      }
      const next = new Set(prev);
      offboardableIds.forEach((id) => next.add(id));
      return next;
    });
  };
  // Track which selected rows are still eligible for the soft offboarding
  // actions (Close / Terminate) — already-closed rows can only be hard-deleted.
  const selectedActiveCount = filteredPublishers.filter(
    (p) => selectedIds.has(p.id) && p.status !== 'account_closed' && p.status !== 'terminated'
  ).length;

  return (
    <AdminShell
      pageTitle="Publishers"
      pageDescription="Review applications, fraud signals, and AI ID verification."
      rightSlot={
        <div className="flex items-center gap-2">
          <button
            onClick={() => setHidePII((v) => !v)}
            className={`flex items-center gap-1.5 px-3 h-9 rounded-md text-[13px] font-medium border transition-all ${
              hidePII
                ? 'bg-amber-500/15 border-amber-500/40 text-amber-300 hover:bg-amber-500/25'
                : 'bg-slate-800/50 border-slate-700 text-slate-300 hover:text-white hover:bg-slate-800'
            }`}
            data-testid="admin-pii-toggle"
            title={hidePII ? 'Show publisher names & emails' : 'Hide publisher names & emails (for screenshares)'}
          >
            {hidePII ? (
              <EyeOff className="w-3.5 h-3.5" strokeWidth={2.25} />
            ) : (
              <Eye className="w-3.5 h-3.5" strokeWidth={2.25} />
            )}
            {hidePII ? 'PII hidden' : 'Hide PII'}
          </button>
          <button
            onClick={async () => {
              if (!window.confirm(
                "Recompute hardware duplicate flags using the tightened rules?\n\n" +
                "This scans every publisher record and updates their duplicate_hardware_flag " +
                "and risk_level per the current logic. Safe to run any time.")) return;
              try {
                const resp = await authFetch('/api/admin/recompute-hardware-flags', { method: 'POST' });
                if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                const data = await resp.json();
                alert(
                  `Hardware flags recomputed:\n\n` +
                  `Scanned: ${data.total_scanned}\n` +
                  `Rows updated: ${data.updated}\n` +
                  `Newly flagged: ${data.newly_flagged}\n` +
                  `Cleared (false positives removed): ${data.newly_cleared}\n` +
                  `Risk level changed: ${data.risk_level_changed}`
                );
                fetchPublishers();
              } catch (e) {
                alert(`Recompute failed: ${e.message}`);
              }
            }}
            className="flex items-center gap-1.5 px-3 h-9 rounded-md text-[13px] font-medium text-indigo-300 hover:text-white bg-indigo-500/10 hover:bg-indigo-500/20 border border-indigo-500/40 hover:border-indigo-500/60 transition-all"
            data-testid="admin-recompute-hw-btn"
            title="Re-run the tightened hardware-duplicate rules across every existing publisher record"
          >
            <ShieldAlert className="w-3.5 h-3.5" strokeWidth={2.25} />
            Recompute HW
          </button>
          <button
            onClick={fetchPublishers}
            className="flex items-center gap-1.5 px-3 h-9 rounded-md text-[13px] font-medium text-slate-300 hover:text-white hover:bg-slate-800/70 border border-slate-800 hover:border-slate-700 transition-all"
            data-testid="admin-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} strokeWidth={2.25} />
            Refresh
          </button>
        </div>
      }
    >
        {/* Stats — Swiss grid: compact, high contrast, tabular-nums */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
          <StatCard label="Applications" value={publishers.length} icon={FileText} tone="neutral" tip="Total rows (one per submission)" />
          <StatCard label="Unique Emails" value={new Set(publishers.map(p => (p.email || '').toLowerCase().trim()).filter(Boolean)).size} icon={Mail} tone="info" tip="Distinct emails — matches the Campaigns page." />
          <StatCard label="Pending" value={publishers.filter(p => p.status === 'pending_review').length} icon={Clock} tone="warning" />
          <StatCard label="On Hold" value={publishers.filter(p => p.status === 'on_hold').length} icon={PauseCircle} tone="warning" />
          <StatCard label="Approved" value={publishers.filter(p => p.status === 'approved').length} icon={CheckCircle} tone="success" />
          <StatCard label="Disapproved" value={publishers.filter(p => p.status === 'blocked').length} icon={Ban} tone="danger" />
          <StatCard label="Fraud Flagged" value={publishers.filter(p => p.fraud_flag).length} icon={AlertTriangle} tone="danger" />
        </div>

        {/* Search + Filter bar */}
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="flex-1 relative min-w-[260px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" strokeWidth={2} />
            <input
              type="text"
              placeholder="Search by name, email, or ID type…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 h-10 rounded-md bg-white border border-gray-300 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 text-sm transition"
              data-testid="admin-search-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" strokeWidth={2} />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="bg-white border border-gray-300 rounded-md px-3 h-10 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 transition"
              data-testid="admin-filter-select"
            >
              <option value="all">All Publishers</option>
              <option value="fraud">Fraud Flagged</option>
              <option value="high_risk">High Risk</option>
              <option value="pending">Pending Review</option>
              <option value="on_hold">On Hold</option>
              <option value="approved">Approved</option>
              <option value="blocked">Disapproved</option>
              <option value="account_closed">Closed</option>
              <option value="terminated">Terminated</option>
              <option value="blacklisted">Blacklisted</option>
            </select>
          </div>
        </div>

        {/* Bulk action bar — appears once 1+ rows are selected */}
        {selectedIds.size > 0 && (
          <div
            className="sticky top-2 z-30 mb-3 flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg bg-slate-900 text-white shadow-lg"
            data-testid="bulk-action-bar"
          >
            <div className="flex items-center gap-3 text-sm">
              <ClipboardCheck className="w-4 h-4" strokeWidth={2.25} />
              <span data-testid="bulk-selected-count">
                <strong>{selectedIds.size}</strong> selected
              </span>
              <button
                type="button"
                onClick={() => setSelectedIds(new Set())}
                className="text-xs text-slate-300 hover:text-white underline underline-offset-2"
                data-testid="bulk-clear-selection"
              >
                Clear
              </button>
            </div>
            <div className="flex items-center gap-2">
              {selectedActiveCount > 0 && (
                <button
                  type="button"
                  onClick={() => handleBulkAction('close')}
                  disabled={bulkRunning}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-700 hover:bg-slate-600 text-white text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition"
                  data-testid="bulk-close-btn"
                  title="Voluntary closure — polite email"
                >
                  {bulkRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}
                  Close {selectedActiveCount}
                </button>
              )}
              {selectedActiveCount > 0 && (
                <button
                  type="button"
                  onClick={() => handleBulkAction('terminate')}
                  disabled={bulkRunning}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-red-600 hover:bg-red-700 text-white text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition"
                  data-testid="bulk-terminate-btn"
                  title="Breach of agreement — formal termination, payouts forfeited"
                >
                  {bulkRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Gavel className="w-3.5 h-3.5" />}
                  Terminate {selectedActiveCount}
                </button>
              )}
              <button
                type="button"
                onClick={() => handleBulkAction('delete')}
                disabled={bulkRunning}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-red-900 hover:bg-red-950 text-white text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition border border-red-700"
                data-testid="bulk-delete-btn"
                title="Hard delete — wipe records permanently (no email, no audit trail)"
              >
                {bulkRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                Delete {selectedIds.size}
              </button>
            </div>
          </div>
        )}

        {/* Publishers Table — dense, professional, light */}
        <div className="rounded-lg border border-gray-200 bg-white overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="w-10 px-3 py-3">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      ref={(el) => { if (el) el.indeterminate = someVisibleSelected; }}
                      onChange={toggleSelectAllVisible}
                      disabled={offboardableIds.length === 0}
                      className="w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-2 focus:ring-slate-900/20 cursor-pointer disabled:opacity-40"
                      title={offboardableIds.length === 0 ? 'No selectable rows' : 'Select all visible'}
                      data-testid="select-all-checkbox"
                    />
                  </th>
                  <th className="text-left px-5 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-[0.08em]">Publisher</th>
                  <th className="text-left px-5 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-[0.08em]">Source</th>
                  <th className="text-left px-5 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-[0.08em]">ID Info</th>
                  <th className="text-left px-5 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-[0.08em]">Risk</th>
                  <th className="text-left px-5 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-[0.08em]">Flags</th>
                  <th className="text-left px-5 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-[0.08em]">Status</th>
                  <th className="text-left px-5 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-[0.08em]">Dates</th>
                  <th className="text-right px-5 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-[0.08em]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredPublishers.map((pub) => {
                  const offboarded = pub.status === 'account_closed' || pub.status === 'terminated';
                  return (
                  <tr
                    key={pub.id}
                    className={`group transition-colors ${pub.fraud_flag ? 'bg-red-50/40 hover:bg-red-50' : 'hover:bg-gray-50'} ${selectedIds.has(pub.id) ? 'bg-slate-50' : ''}`}
                  >
                    <td className="px-3 py-3.5">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(pub.id)}
                        onChange={() => toggleRowSelection(pub.id)}
                        className="w-4 h-4 rounded border-slate-300 text-slate-900 focus:ring-2 focus:ring-slate-900/20 cursor-pointer"
                        title={offboarded ? 'Already offboarded — can still be hard-deleted' : 'Select this publisher'}
                        data-testid={`row-checkbox-${pub.id}`}
                      />
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-xs ${pub.fraud_flag ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-700'}`}>
                          {hidePII ? '••' : (pub.full_name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-slate-900 flex items-center gap-1.5 text-sm" data-testid={`row-name-${pub.id}`}>
                            {maskName(pub.full_name)}
                            {pub.fraud_flag && (
                              <AlertTriangle className="w-3.5 h-3.5 text-red-500" strokeWidth={2.25} />
                            )}
                          </p>
                          <p className="text-xs text-slate-500 truncate max-w-[240px]" data-testid={`row-email-${pub.id}`}>{maskEmail(pub.email)}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3.5" data-testid={`row-source-${pub.id}`}>
                      {(() => {
                        const ref = (pub.referring_url || "").trim();
                        if (!ref) {
                          return (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-slate-100 border border-slate-200 text-[11px] font-medium text-slate-500">
                              Direct
                            </span>
                          );
                        }
                        let host = ref;
                        try {
                          host = new URL(ref).hostname.replace(/^www\./, "");
                        } catch {
                          /* not a valid URL — keep raw string */
                        }
                        return (
                          <a
                            href={ref}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={ref}
                            className="inline-flex items-center px-2 py-0.5 rounded-md bg-blue-50 border border-blue-200 text-[11px] font-medium text-blue-700 hover:bg-blue-100 hover:border-blue-300 transition max-w-[180px] truncate"
                          >
                            {host}
                          </a>
                        );
                      })()}
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-slate-100 border border-slate-200 text-[11px] font-medium text-slate-700">
                        <IdCard className="w-3 h-3 text-slate-400" strokeWidth={2.25} />
                        {pub.id_type || 'N/A'}
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      {getRiskBadge(pub.risk_level)}
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex flex-wrap gap-1" data-testid={`row-flags-${pub.id}`}>
                        {(() => {
                          // Unified flag summary — one "Fraud [N]" chip whose
                          // color reflects the highest severity of any active
                          // flag on the row. Keeps the list scannable while
                          // preserving the full detail in the drawer.
                          const highFlags = [
                            pub.super_duplicate_flag && 'CONFIRMED',
                            pub.duplicate_hardware_flag && 'HW',
                            pub.duplicate_id_flag && 'ID',
                            pub.previously_blocked_flag && 'PREV. DISAPPROVED',
                            pub.is_vpn_flag && 'VPN',
                            pub.is_proxy_flag && 'PROXY',
                            pub.is_datacenter_flag && 'DC',
                            pub.velocity_bot_flag && 'BOT',
                          ].filter(Boolean);
                          const medFlags = [
                            pub.duplicate_ip_flag && 'IP',
                            pub.duplicate_fingerprint_flag && 'FP',
                            pub.geo_mismatch_flag && 'GEO',
                          ].filter(Boolean);
                          const isBl = !!pub.blacklisted;

                          if (pub.super_duplicate_flag) {
                            return (
                              <span
                                className="px-2 py-0.5 bg-red-600 text-white border border-red-700 rounded-full text-[10px] font-bold tracking-wider animate-pulse shadow-sm shadow-red-500/50 uppercase inline-flex items-center gap-1"
                                title={`Confirmed duplicate · ${highFlags.length + medFlags.length} flags total`}
                                data-testid={`flag-super-${pub.id}`}
                              >
                                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                                Confirmed
                              </span>
                            );
                          }
                          if (highFlags.length) {
                            return (
                              <span
                                className="px-2 py-0.5 bg-red-500/15 text-red-300 border border-red-500/40 rounded-full text-[10px] font-bold tracking-wider uppercase inline-flex items-center gap-1"
                                title={`High-risk flags: ${highFlags.join(', ')}`}
                                data-testid={`flag-high-${pub.id}`}
                              >
                                <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
                                Fraud · {highFlags.length + medFlags.length}
                              </span>
                            );
                          }
                          if (medFlags.length) {
                            return (
                              <span
                                className="px-2 py-0.5 bg-amber-500/15 text-amber-300 border border-amber-500/40 rounded-full text-[10px] font-bold tracking-wider uppercase inline-flex items-center gap-1"
                                title={`Review needed: ${medFlags.join(', ')}`}
                                data-testid={`flag-medium-${pub.id}`}
                              >
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                                Review · {medFlags.length}
                              </span>
                            );
                          }
                          if (isBl) {
                            return (
                              <span
                                className="px-2 py-0.5 bg-rose-500/15 text-rose-300 border border-rose-500/40 rounded-full text-[10px] font-bold tracking-wider uppercase inline-flex items-center gap-1"
                                data-testid={`flag-blacklisted-${pub.id}`}
                              >
                                <ShieldAlert className="w-2.5 h-2.5" strokeWidth={2.5} />
                                Blacklisted
                              </span>
                            );
                          }
                          return <span className="text-slate-500 text-xs">—</span>;
                        })()}
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      {getStatusBadge(pub.status)}
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex flex-col gap-0.5 text-[11px] leading-tight" data-testid={`row-dates-${pub.id}`}>
                        <span
                          className="text-slate-700"
                          title={formatDateTime(pub.submitted_at) || ''}
                          data-testid={`row-applied-${pub.id}`}
                        >
                          <span className="text-slate-400">Applied:</span>{' '}
                          {formatDateShort(pub.submitted_at) || '—'}
                        </span>
                        <span
                          className={pub.approved_at ? 'text-emerald-700' : 'text-slate-300'}
                          title={formatDateTime(pub.approved_at) || ''}
                          data-testid={`row-approved-at-${pub.id}`}
                        >
                          <span className="text-slate-400">Approved:</span>{' '}
                          {formatDateShort(pub.approved_at) || '—'}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          onClick={() => setSelectedPublisher(pub)}
                          className="p-1.5 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
                          title="View details"
                          data-testid={`view-publisher-${pub.id}`}
                        >
                          <Eye className="w-4 h-4" strokeWidth={2} />
                        </button>
                        {pub.status !== 'approved' && !offboarded && (
                          <button
                            onClick={() => handleApprove(pub.id)}
                            className="p-1.5 rounded-md text-emerald-600 hover:text-white hover:bg-emerald-600 transition-all"
                            title="Approve"
                            data-testid={`row-approve-${pub.id}`}
                          >
                            <Check className="w-4 h-4" strokeWidth={2.25} />
                          </button>
                        )}
                        {pub.status !== 'on_hold' && !offboarded && (
                          <button
                            onClick={() => handleHold(pub.id)}
                            className="p-1.5 rounded-md text-orange-600 hover:text-white hover:bg-orange-600 transition-all"
                            title="Hold (wrong / incomplete info)"
                            data-testid={`row-hold-${pub.id}`}
                          >
                            <PauseCircle className="w-4 h-4" strokeWidth={2.25} />
                          </button>
                        )}
                        {pub.status !== 'blocked' && !offboarded && (
                          <button
                            onClick={() => handleBlock(pub.id)}
                            className="p-1.5 rounded-md text-red-600 hover:text-white hover:bg-red-600 transition-all"
                            title="Disapprove"
                            data-testid={`row-block-${pub.id}`}
                          >
                            <Ban className="w-4 h-4" strokeWidth={2.25} />
                          </button>
                        )}
                        {!offboarded && (
                          <button
                            onClick={() => { setSelectedPublisher(pub); }}
                            className="p-1.5 rounded-md text-slate-600 hover:text-white hover:bg-slate-700 transition-all"
                            title="Close account (voluntary) — opens detail to confirm"
                            data-testid={`row-close-${pub.id}`}
                          >
                            <XCircle className="w-4 h-4" strokeWidth={2.25} />
                          </button>
                        )}
                        {!offboarded && (
                          <button
                            onClick={() => { setSelectedPublisher(pub); }}
                            className="p-1.5 rounded-md text-red-700 hover:text-white hover:bg-red-700 transition-all"
                            title="Terminate (breach of agreement) — opens detail to confirm"
                            data-testid={`row-terminate-${pub.id}`}
                          >
                            <Gavel className="w-4 h-4" strokeWidth={2.25} />
                          </button>
                        )}
                        {/* Quick blacklist — one click, no modal. Shows a
                            filled shield when already blacklisted. */}
                        <button
                          onClick={() => handleQuickBlacklist(pub)}
                          disabled={pub.blacklisted}
                          className={`p-1.5 rounded-md transition-all ${
                            pub.blacklisted
                              ? 'text-rose-300 bg-rose-500/15 cursor-default'
                              : 'text-rose-600 hover:text-white hover:bg-rose-600'
                          }`}
                          title={pub.blacklisted ? 'Already on the blacklist' : 'Add to blacklist (quick — no modal)'}
                          data-testid={`row-blacklist-${pub.id}`}
                        >
                          <ShieldAlert className="w-4 h-4" strokeWidth={2.25} />
                        </button>
                        {/* Edit — always available so admins can fix typos even after offboarding */}
                        <button
                          onClick={() => openEditModal(pub)}
                          className="p-1.5 rounded-md text-blue-600 hover:text-white hover:bg-blue-600 transition-all"
                          title="Edit publisher details"
                          data-testid={`row-edit-${pub.id}`}
                        >
                          <Edit3 className="w-4 h-4" strokeWidth={2.25} />
                        </button>
                        {/* Reactivate — visible only when publisher is offboarded (blocked / terminated / account_closed) */}
                        {offboarded && (
                          <button
                            onClick={() => handleReactivate(pub.id)}
                            className="p-1.5 rounded-md text-emerald-600 hover:text-white hover:bg-emerald-600 transition-all"
                            title="Undo — reactivate this publisher (resets to Pending Review)"
                            data-testid={`row-reactivate-${pub.id}`}
                          >
                            <RotateCcw className="w-4 h-4" strokeWidth={2.25} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredPublishers.length === 0 && (
            <div className="text-center py-16 px-4">
              <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <Search className="w-6 h-6 text-slate-400" strokeWidth={2} />
              </div>
              <p className="text-slate-700 font-semibold">No publishers match your filter</p>
              <p className="text-slate-500 text-sm mt-1">Try adjusting your search or filter above.</p>
            </div>
          )}
        </div>

        {/* Publisher Detail — centered modal (was side-drawer) with sticky action footer */}
        {selectedPublisher && (
          <div
            className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm z-50 flex justify-center items-center p-2 sm:p-4 animate-in fade-in duration-150"
            onClick={(e) => { if (e.target === e.currentTarget) setSelectedPublisher(null); }}
            data-testid="publisher-details-drawer"
          >
            <div className="relative bg-slate-950 w-[800px] max-w-full max-h-[92vh] rounded-2xl border border-slate-800 shadow-2xl flex flex-col overflow-hidden text-[13px] leading-snug" data-testid="publisher-modal-panel">
              {/* Left accent rail — risk color */}
              <div className={`absolute left-0 top-0 bottom-0 w-[3px] rounded-l-2xl ${
                selectedPublisher.risk_level === 'HIGH' ? 'bg-red-500' :
                selectedPublisher.risk_level === 'MEDIUM' ? 'bg-amber-500' : 'bg-slate-700'
              }`} />
              <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 [&_p]:text-slate-200 [&_label]:text-slate-400 [&_h3]:text-slate-100 [&_h2]:text-white [&_.text-gray-500]:text-slate-500 [&_.text-slate-500]:text-slate-500 [&_.text-slate-600]:text-slate-400 [&_.text-slate-700]:text-slate-200 [&_.text-slate-900]:text-white [&_.text-black]:text-white [&_.border-gray-200]:border-slate-800 [&_.border-slate-200]:border-slate-800 [&_.bg-white]:bg-slate-900 [&_.bg-slate-50]:bg-slate-900/60 [&_.bg-slate-50\/60]:bg-slate-900/60 [&_.bg-gray-50]:bg-slate-900/60 [&_.bg-slate-100]:bg-slate-800 [&_input]:bg-slate-900 [&_input]:border-slate-700 [&_input]:text-slate-100 [&_select]:bg-slate-900 [&_select]:border-slate-700 [&_select]:text-slate-100 [&_textarea]:bg-slate-900 [&_textarea]:border-slate-700 [&_textarea]:text-slate-100">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center font-semibold text-slate-100 ring-1 ring-slate-700 text-sm shrink-0">
                      {(selectedPublisher.full_name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <h2 className="font-display text-base font-bold text-white tracking-tight flex items-center truncate">
                        {selectedPublisher.full_name}
                        <CopyButton
                          value={selectedPublisher.full_name}
                          label="name"
                          testid="copy-name"
                        />
                      </h2>
                      <p className="text-[11px] text-slate-400 flex items-center font-mono truncate">
                        {selectedPublisher.email}
                        <CopyButton
                          value={selectedPublisher.email}
                          label="email"
                          testid="copy-email-header"
                        />
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        const p = selectedPublisher;
                        // Professional, share-ready status labels (WhatsApp/Telegram friendly).
                        const statusLabel = {
                          approved: 'Approved',
                          rejected: 'Disapproved',
                          blocked: 'Disapproved',
                          terminated: 'Terminated',
                          pending_review: 'Pending Review',
                          on_hold: 'On Hold',
                          account_closed: 'Account Closed',
                        }[p.status] || (p.status ? p.status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '');
                        const lines = [
                          `Name: ${p.full_name || ''}`,
                          `Email: ${p.email || ''}`,
                          `Contact: ${p.contact_method || ''}`,
                          p.messaging_platform && p.messaging_handle
                            ? `${p.messaging_platform}: ${p.messaging_handle}`
                            : null,
                          `Country: ${p.country || ''}`,
                          p.company_name ? `Company: ${p.company_name}` : null,
                          `ID Type: ${p.id_type || 'N/A'}`,
                          `Risk: ${p.risk_level || ''}`,
                          `Status: ${statusLabel}`,
                        ].filter(Boolean);
                        const text = lines.join('\n');
                        if (navigator?.clipboard?.writeText) {
                          navigator.clipboard.writeText(text);
                        }
                      }}
                      data-testid="copy-all-details-btn"
                      title="Copy all details"
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-md hover:bg-slate-50 hover:border-slate-300 transition-colors"
                    >
                      <Copy className="w-3.5 h-3.5" />
                      Copy all
                    </button>
                    <button
                      onClick={() => setSelectedPublisher(null)}
                      className="p-2 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
                    >
                      <XCircle className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Risk + Timeline — compact side-by-side row */}
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <div className={`rounded-md px-3 py-2 border ${
                    selectedPublisher.risk_level === 'HIGH' ? 'bg-red-50 border-red-200' :
                    selectedPublisher.risk_level === 'MEDIUM' ? 'bg-amber-50 border-amber-200' :
                    'bg-emerald-50 border-emerald-200'
                  }`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[9px] uppercase tracking-[0.14em] text-slate-600 font-semibold">Risk Level</p>
                      {selectedPublisher.fraud_flag && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-red-100 border border-red-200 rounded text-red-700 text-[9px] font-bold tracking-wider">
                          <AlertTriangle className="w-3 h-3" strokeWidth={2.25} />
                          FRAUD
                        </span>
                      )}
                    </div>
                    <p className={`font-display text-xl font-bold tabular-nums leading-tight ${
                      selectedPublisher.risk_level === 'HIGH' ? 'text-red-700' :
                      selectedPublisher.risk_level === 'MEDIUM' ? 'text-amber-700' :
                      'text-emerald-700'
                    }`}>
                      {selectedPublisher.risk_level}
                    </p>
                  </div>
                  <div className="rounded-md px-3 py-2 border border-slate-200 bg-slate-50" data-testid="modal-timeline">
                    <p className="text-[9px] uppercase tracking-[0.14em] text-slate-600 font-semibold mb-1 flex items-center gap-1">
                      <Clock className="w-3 h-3" strokeWidth={2.25} />
                      Timeline
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-[11px]" data-testid="modal-applied-at">
                      <div>
                        <p className="text-slate-500 font-semibold uppercase tracking-wider text-[9px]">Applied</p>
                        <p className="text-slate-900 font-medium font-mono">
                          {formatDateTime(selectedPublisher.submitted_at) || '—'}
                        </p>
                      </div>
                      <div data-testid="modal-approved-at">
                        <p className="text-slate-500 font-semibold uppercase tracking-wider text-[9px]">Approved</p>
                        <p className={`font-medium font-mono ${selectedPublisher.approved_at ? 'text-emerald-700' : 'text-slate-400'}`}>
                          {formatDateTime(selectedPublisher.approved_at) || 'Pending'}
                        </p>
                      </div>
                      {selectedPublisher.terminated_at && (
                        <div data-testid="modal-terminated-at">
                          <p className="text-slate-500 font-semibold uppercase tracking-wider text-[9px]">Terminated</p>
                          <p className="text-red-700 font-medium font-mono">
                            {formatDateTime(selectedPublisher.terminated_at)}
                          </p>
                        </div>
                      )}
                      {selectedPublisher.closed_at && (
                        <div data-testid="modal-closed-at">
                          <p className="text-slate-500 font-semibold uppercase tracking-wider text-[9px]">Closed</p>
                          <p className="text-slate-700 font-medium font-mono">
                            {formatDateTime(selectedPublisher.closed_at)}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Details Grid — strict 2-column: Column 1 Identity/Contact · Column 2 Traffic/Proof */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <h3 className="font-semibold mb-1.5 text-slate-900 text-[11px] uppercase tracking-[0.08em] pb-1 border-b border-slate-800 flex items-center gap-1.5"><span className="w-1 h-2.5 bg-blue-500 rounded-sm inline-block"/>Identity</h3>
                    <div className="space-y-1 text-[12px] text-slate-900 font-mono">
                      <p>
                        <span className="text-slate-400">Email:</span> {selectedPublisher.email}
                        <CopyButton value={selectedPublisher.email} label="email" testid="copy-email" />
                      </p>
                      <p data-testid="admin-phone">
                        <span className="text-slate-400">Phone:</span>{' '}
                        {selectedPublisher.phone ? (
                          <>
                            <span className="font-mono">{selectedPublisher.phone}</span>
                            <CopyButton value={selectedPublisher.phone} label="phone" testid="copy-phone" />
                          </>
                        ) : (
                          <span className="text-slate-400 italic">Not provided</span>
                        )}
                      </p>
                      <p>
                        <span className="text-slate-400">Contact:</span> {selectedPublisher.contact_method}
                        <CopyButton value={selectedPublisher.contact_method} label="contact" testid="copy-contact" />
                      </p>
                      <p data-testid="admin-messaging-platform">
                        <span className="text-slate-400">
                          {selectedPublisher.messaging_platform || 'Teams / Telegram'}:
                        </span>{' '}
                        {selectedPublisher.messaging_handle ? (
                          <>
                            <span className="font-mono">{selectedPublisher.messaging_handle}</span>
                            <CopyButton
                              value={selectedPublisher.messaging_handle}
                              label="messaging handle"
                              testid="copy-messaging-handle"
                            />
                          </>
                        ) : (
                          <span className="text-slate-400 italic">Not provided</span>
                        )}
                      </p>
                      <p>
                        <span className="text-slate-400">Country:</span> {selectedPublisher.country}
                      </p>
                      <p>
                        <span className="text-slate-400">Company:</span> {selectedPublisher.company_name || 'N/A'}
                        <CopyButton value={selectedPublisher.company_name} label="company" testid="copy-company" />
                      </p>
                      <p><span className="text-slate-400">ID Type:</span> {selectedPublisher.id_type || 'N/A'}</p>
                      <p><span className="text-slate-400">ID File:</span> {(selectedPublisher.id_file_path || selectedPublisher.id_cloudinary_public_id) ? 'Uploaded' : 'Not uploaded'}</p>
                      {selectedPublisher.ai_id_decision && (
                        <div className="mt-1 rounded border border-gray-200 bg-gray-50 p-2 text-[11px] font-sans">
                          <p className="flex items-center gap-1.5 mb-0.5">
                            <span className="text-slate-400">AI Verdict:</span>
                            <span className={`px-1.5 py-0.5 rounded font-semibold text-[10px] ${
                              selectedPublisher.ai_id_decision === 'VALID'
                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                : selectedPublisher.ai_id_decision === 'UNCERTAIN'
                                ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                : 'bg-red-50 text-red-700 border border-red-200'
                            }`}>
                              {selectedPublisher.ai_id_decision}
                            </span>
                            {typeof selectedPublisher.ai_id_verdict?.confidence === 'number' && (
                              <span className="text-slate-400">
                                {(selectedPublisher.ai_id_verdict.confidence * 100).toFixed(0)}%
                              </span>
                            )}
                          </p>
                          {selectedPublisher.ai_id_verdict && (
                            <div className="space-y-0.5 text-slate-400">
                              <p>Detected: <span className="text-slate-800">{selectedPublisher.ai_id_verdict.id_type_detected || 'Unknown'}</span></p>
                              <p>Name on ID: <span className="text-slate-800">{selectedPublisher.ai_id_verdict.name_on_id || '—'}</span></p>
                              <p>Match: {selectedPublisher.ai_id_verdict.name_match === true ? <span className="text-emerald-600">yes</span> : selectedPublisher.ai_id_verdict.name_match === false ? <span className="text-red-600">no</span> : <span>n/a</span>}</p>
                              {selectedPublisher.ai_id_verdict.reason && (
                                <p className="text-slate-300 italic">&ldquo;{selectedPublisher.ai_id_verdict.reason}&rdquo;</p>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold mb-1.5 text-slate-900 text-[11px] uppercase tracking-[0.08em] pb-1 border-b border-slate-800 flex items-center gap-1.5"><span className="w-1 h-2.5 bg-blue-500 rounded-sm inline-block"/>Traffic Source &amp; Proof</h3>
                    <div className="space-y-1 text-[12px] text-slate-900">
                      {selectedPublisher.source_details && Object.keys(selectedPublisher.source_details).length > 0 && (
                        <div className="rounded-lg border border-slate-700 bg-slate-800/60 p-3 text-xs" data-testid="pub-source-details">
                          {(() => {
                            const key = (selectedPublisher.primary_traffic_source || '').toLowerCase();
                            const fromConfig = PRIMARY_SOURCES.find((s) => s.key === key);
                            const first = Array.isArray(selectedPublisher.traffic_sources)
                              ? (selectedPublisher.traffic_sources[0] || '')
                              : '';
                            const label = fromConfig
                              ? fromConfig.label
                              : (key || first)
                                  .replace(/_/g, ' ')
                                  .split(' ')
                                  .filter(Boolean)
                                  .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
                                  .join(' ');
                            return label ? (
                              <div
                                className="text-white font-bold text-sm mb-2 pb-2 border-b border-slate-700"
                                data-testid="pub-source-heading"
                              >
                                Traffic Source: <span className="text-blue-300">{label}</span>
                              </div>
                            ) : null;
                          })()}
                          <div className="text-slate-300 uppercase tracking-widest font-semibold text-[10px] mb-1.5">Source Details</div>
                          <div className="grid grid-cols-1 gap-1.5">
                            {Object.entries(selectedPublisher.source_details).map(([k, v]) => (
                              <div key={k} className="flex gap-2 items-start">
                                <span className="text-slate-300 whitespace-nowrap min-w-[110px] font-medium">{({
                                  gmb_listings_count: "Verified Listings Count",
                                  gmb_business_category: "Business Category",
                                  gmb_service_areas: "Service Areas (US States)",
                                  gmb_dashboard_proof: "GMB Dashboard Screenshot",
                                  gmb_profile_link: "Live GMB Profile",
                                }[k]) || <span className="capitalize">{k.replace(/_/g, " ")}</span>}:</span>
                                <div className="text-white break-all flex-1">
                                  {Array.isArray(v) ? (
                                    <ul className="list-disc list-inside space-y-0.5" data-testid={`pub-source-list-${k}`}>
                                      {v.filter((item) => item !== null && item !== "").map((item, i) => (
                                        <li key={i}>
                                          {typeof item === "string" && /^https?:\/\//i.test(item) ? (
                                            <a href={item} target="_blank" rel="noopener noreferrer" className="text-blue-300 hover:underline">{item}</a>
                                          ) : (
                                            String(item)
                                          )}
                                        </li>
                                      ))}
                                    </ul>
                                  ) : typeof v === "string" && /^https?:\/\//i.test(v) ? (
                                    <a href={v} target="_blank" rel="noopener noreferrer" className="text-blue-300 hover:underline">{v}</a>
                                  ) : (
                                    String(v)
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {Array.isArray(selectedPublisher.proof_urls) && selectedPublisher.proof_urls.length > 0 ? (
                        <div data-testid="pub-proof-gallery">
                          <div className="text-slate-400 uppercase tracking-widest font-semibold text-[10px] mb-1.5">Proof of Work ({selectedPublisher.proof_urls.length})</div>
                          <div className="grid grid-cols-3 gap-2">
                            {selectedPublisher.proof_urls.map((rawUrl, i) => {
                              const isPdf = /\.pdf(\?.*)?$/i.test(rawUrl);
                              const displayUrl = proofBlobUrls[i] || null;
                              return (
                                <a
                                  key={i}
                                  href={displayUrl || "#"}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => { if (!displayUrl) e.preventDefault(); }}
                                  className="block group relative rounded-lg overflow-hidden border border-slate-200 hover:border-blue-500 transition-all bg-slate-50 aspect-square"
                                  data-testid={`pub-proof-${i}`}
                                >
                                  {isPdf ? (
                                    <div className="w-full h-full flex flex-col items-center justify-center text-slate-500">
                                      <FileText className="w-8 h-8 mb-1" />
                                      <span className="text-[9px] uppercase tracking-widest">PDF</span>
                                    </div>
                                  ) : displayUrl ? (
                                    <img src={displayUrl} alt={`proof-${i}`} className="w-full h-full object-cover group-hover:opacity-90" loading="lazy" />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center text-slate-400 text-[10px]">
                                      <Loader2 className="w-5 h-5 animate-spin" />
                                    </div>
                                  )}
                                </a>
                              );
                            })}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold mb-3 text-slate-900 text-[13px] uppercase tracking-[0.08em] pb-2 border-b border-slate-800 flex items-center gap-2"><span className="w-1 h-3 bg-slate-500 rounded-sm inline-block"/>Technical Details</h3>
                    <div className="space-y-2 text-sm">
                      <p><span className="text-slate-400">IP Address:</span> <span className="font-mono">{selectedPublisher.ip_address || 'N/A'}</span></p>
                      <p><span className="text-slate-400">Fingerprint:</span> <span className="font-mono text-xs">{selectedPublisher.fingerprint_id || 'N/A'}</span></p>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold mb-3 text-slate-900 text-[13px] uppercase tracking-[0.08em] pb-2 border-b border-slate-800 flex items-center gap-2"><span className="w-1 h-3 bg-red-500 rounded-sm inline-block"/>Fraud Analysis</h3>
                    <div className="space-y-2 text-sm">
                      <p className="flex items-center gap-2">
                        {selectedPublisher.duplicate_ip_flag ? <XCircle className="w-4 h-4 text-red-600" /> : <CheckCircle className="w-4 h-4 text-emerald-600" />}
                        Duplicate IP: {selectedPublisher.duplicate_ip_flag ? 'Yes' : 'No'}
                      </p>
                      {selectedPublisher.duplicate_ip_flag && selectedPublisher.duplicate_matches?.ip && (
                        <DuplicateMatchCard kind="IP" match={selectedPublisher.duplicate_matches.ip} />
                      )}

                      <p className="flex items-center gap-2">
                        {selectedPublisher.duplicate_fingerprint_flag ? <XCircle className="w-4 h-4 text-red-600" /> : <CheckCircle className="w-4 h-4 text-emerald-600" />}
                        Duplicate Fingerprint: {selectedPublisher.duplicate_fingerprint_flag ? 'Yes' : 'No'}
                      </p>
                      {selectedPublisher.duplicate_fingerprint_flag && selectedPublisher.duplicate_matches?.fingerprint && (
                        <DuplicateMatchCard kind="Fingerprint" match={selectedPublisher.duplicate_matches.fingerprint} />
                      )}

                      <p className="flex items-center gap-2">
                        {selectedPublisher.duplicate_id_flag ? <XCircle className="w-4 h-4 text-red-600" /> : <CheckCircle className="w-4 h-4 text-emerald-600" />}
                        Duplicate ID: {selectedPublisher.duplicate_id_flag ? 'Yes' : 'No'}
                      </p>
                      {selectedPublisher.duplicate_id_flag && selectedPublisher.duplicate_matches?.id_number && (
                        <DuplicateMatchCard kind="ID" match={selectedPublisher.duplicate_matches.id_number} />
                      )}

                      <p className="flex items-center gap-2">
                        {selectedPublisher.super_duplicate_flag ? <XCircle className="w-4 h-4 text-red-600" /> : <CheckCircle className="w-4 h-4 text-emerald-600" />}
                        Super Hardware ID (CPU + RAM + audio + fonts + screen + media):{' '}
                        {selectedPublisher.super_duplicate_flag ? 'MATCH ⚠' : 'Unique'}
                        {selectedPublisher.super_duplicate_flag && (
                          <span className="ml-1 px-1.5 py-0.5 rounded text-[9.5px] font-bold bg-red-600 text-white border border-red-700 uppercase tracking-wider animate-pulse">
                            ⭐ 100% CONFIRMED
                          </span>
                        )}
                      </p>
                      {selectedPublisher.super_duplicate_flag && selectedPublisher.duplicate_matches?.super_hardware && (
                        <div className="ml-6 mt-1">
                          <DuplicateMatchCard
                            kind="Super Hardware · exact match"
                            match={selectedPublisher.duplicate_matches.super_hardware}
                          />
                        </div>
                      )}

                      <p className="flex items-center gap-2">
                        {selectedPublisher.duplicate_hardware_flag ? <XCircle className="w-4 h-4 text-red-600" /> : <CheckCircle className="w-4 h-4 text-emerald-600" />}
                        Duplicate Hardware (different email, same device):{' '}
                        {selectedPublisher.duplicate_hardware_flag ? 'Yes' : 'No'}
                        {selectedPublisher.duplicate_hardware_flag && (
                          <span className="ml-1 px-1.5 py-0.5 rounded text-[9.5px] font-bold bg-red-600 text-white border border-red-700 uppercase tracking-wider">
                            ⚠ VPN-proof
                          </span>
                        )}
                      </p>
                      {selectedPublisher.duplicate_hardware_flag && selectedPublisher.duplicate_matches?.hardware && (
                        <div className="ml-6 mt-1">
                          <DuplicateMatchCard
                            kind={`Hardware · matched ${(selectedPublisher.duplicate_matches.hardware.matched_signals || []).join(' + ') || 'device'}`}
                            match={selectedPublisher.duplicate_matches.hardware}
                          />
                        </div>
                      )}

                      <p className="flex items-center gap-2">
                        {(selectedPublisher.is_vpn_flag || selectedPublisher.is_proxy_flag || selectedPublisher.is_datacenter_flag) ? <XCircle className="w-4 h-4 text-red-600" /> : <CheckCircle className="w-4 h-4 text-emerald-600" />}
                        VPN / Proxy / Datacenter IP:{' '}
                        {selectedPublisher.is_vpn_flag ? 'VPN' :
                         selectedPublisher.is_proxy_flag ? `Proxy${selectedPublisher.proxy_type ? ` (${selectedPublisher.proxy_type})` : ''}` :
                         selectedPublisher.is_datacenter_flag ? 'Datacenter' : 'Clean'}
                      </p>

                      <p className="flex items-center gap-2">
                        {selectedPublisher.velocity_bot_flag ? <XCircle className="w-4 h-4 text-red-600" /> : <CheckCircle className="w-4 h-4 text-emerald-600" />}
                        Form-fill velocity:{' '}
                        {typeof selectedPublisher.form_duration_seconds === 'number'
                          ? `${selectedPublisher.form_duration_seconds.toFixed(1)}s`
                          : '—'}
                        {selectedPublisher.velocity_bot_flag && (
                          <span className="ml-1 px-1.5 py-0.5 rounded text-[9.5px] font-bold bg-red-600 text-white border border-red-700 uppercase tracking-wider">
                            ⚡ Velocity-Bot
                          </span>
                        )}
                      </p>

                      <p className="flex items-center gap-2">
                        {selectedPublisher.geo_mismatch_flag ? <XCircle className="w-4 h-4 text-amber-600" /> : <CheckCircle className="w-4 h-4 text-emerald-600" />}
                        Geo match: form={selectedPublisher.country || '—'}
                        {selectedPublisher.ip_country_code && (
                          <> · IP={selectedPublisher.ip_country_code}</>
                        )}
                        {selectedPublisher.geo_mismatch_flag && (
                          <span className="ml-1 px-1.5 py-0.5 rounded text-[9.5px] font-bold bg-amber-100 text-amber-800 border border-amber-300 uppercase tracking-wider">
                            Geo-Mismatch
                          </span>
                        )}
                      </p>

                      <p className="flex items-center gap-2">
                        {selectedPublisher.previously_blocked_flag ? <XCircle className="w-4 h-4 text-red-600" /> : <CheckCircle className="w-4 h-4 text-emerald-600" />}
                        Previously Disapproved: {selectedPublisher.previously_blocked_flag ? 'Yes' : 'No'}
                      </p>
                    </div>

                    {/* Previously-blocked details (only shown when flag is Yes) */}
                    {selectedPublisher.previously_blocked_flag && (
                      <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3 text-xs">
                        {selectedPublisher.previously_blocked_details ? (
                          <div className="space-y-1.5">
                            <p className="text-red-700 font-semibold flex items-center gap-2">
                              <AlertTriangle className="w-3.5 h-3.5" />
                              Matched a previously disapproved applicant
                            </p>
                            <p className="text-slate-700">
                              <span className="text-slate-400">Matched by:</span>{' '}
                              <span className="font-mono">
                                {(selectedPublisher.previously_blocked_details.matched_by || []).join(', ') || 'unknown'}
                              </span>
                            </p>
                            <p className="text-slate-700">
                              <span className="text-slate-400">Prior name:</span>{' '}
                              <span className="text-slate-900">
                                {selectedPublisher.previously_blocked_details.prior_full_name || '—'}
                              </span>
                            </p>
                            <p className="text-slate-700">
                              <span className="text-slate-400">Prior email:</span>{' '}
                              <span className="text-slate-900">
                                {selectedPublisher.previously_blocked_details.prior_email || '—'}
                              </span>
                            </p>
                            <p className="text-slate-700">
                              <span className="text-slate-400">Prior submitted:</span>{' '}
                              <span className="text-slate-900">
                                {selectedPublisher.previously_blocked_details.prior_submitted_at
                                  ? new Date(selectedPublisher.previously_blocked_details.prior_submitted_at).toLocaleString()
                                  : '—'}
                              </span>
                            </p>
                            <p className="text-slate-500 pt-1">
                              App ID:{' '}
                              <span className="font-mono text-slate-400">
                                {selectedPublisher.previously_blocked_details.prior_application_id}
                              </span>
                            </p>
                          </div>
                        ) : (
                          <p className="text-amber-700">
                            Details not recorded for this flag (legacy record — match may be a false positive).
                            New submissions will show which prior record matched and why.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* ID Image — shown for BOTH legacy disk and new Cloudinary records */}
                {(selectedPublisher.id_file_path || selectedPublisher.id_cloudinary_public_id) && (
                  <div className="mt-6">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-slate-900 text-[13px] uppercase tracking-[0.08em]">ID Document</h3>
                      {idImageUrl && (
                        <a
                          href={idImageUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-slate-900 hover:text-slate-700 underline-offset-4 underline font-medium"
                        >
                          Open full size
                        </a>
                      )}
                    </div>
                    {idImageLoading && (
                      <div className="h-48 rounded-md border border-gray-200 bg-gray-50 flex items-center justify-center text-slate-500 text-sm">
                        Loading ID document…
                      </div>
                    )}
                    {!idImageLoading && idImageUrl && (
                      <img
                        src={idImageUrl}
                        alt="ID Document"
                        className="max-w-full h-auto rounded-md border border-gray-200 shadow-sm"
                        data-testid="admin-id-preview"
                      />
                    )}
                    {!idImageLoading && !idImageUrl && (
                      <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" strokeWidth={2.25} />
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-red-700">Could not load ID image</p>
                            {idImageError && (
                              <p className="text-xs text-red-600 mt-1">
                                Status: <span className="font-mono">{idImageError.status || 'network'}</span>
                                {idImageError.message ? <> — {idImageError.message}</> : null}
                              </p>
                            )}
                            <p className="text-xs text-slate-600 mt-2 leading-relaxed">
                              {idImageError?.status === 404
                                ? 'This ID was uploaded before we migrated to Cloudinary cloud storage, and the original file was lost during a previous server redeploy. All IDs uploaded from now on are permanently stored on Cloudinary and will never go missing again. Ask the publisher to re-submit if you still need to verify them.'
                                : idImageError?.status === 401
                                ? 'Your session expired. Please log in again.'
                                : idImageError?.status === 0
                                ? 'Network error — check the browser devtools console or your connection.'
                                : 'Try refreshing, logging out and back in, or redeploying the latest backend.'}
                            </p>
                            <button
                              type="button"
                              onClick={() => setIdImageReloadKey((k) => k + 1)}
                              className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-white hover:bg-gray-50 border border-gray-300 text-xs text-slate-700 font-medium transition-colors"
                              data-testid="id-image-retry"
                            >
                              <RefreshCw className="w-3.5 h-3.5" strokeWidth={2.25} />
                              Retry
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Offer Requests — publisher-specific pending & approved applications */}
                <div className="mt-6 pt-6 border-t border-slate-700/60" data-testid="offer-requests-section">
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <h3 className="font-semibold text-white text-[13px] uppercase tracking-[0.08em] flex items-center gap-2">
                      <Phone className="w-4 h-4 text-blue-400" strokeWidth={2.25} />
                      Pending Offer Requests
                      {(() => {
                        const pendingCount = offerRequests.filter((r) => r.status === 'pending').length;
                        return pendingCount > 0 ? (
                          <span
                            className="bg-amber-500 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full"
                            data-testid="pending-offer-count"
                          >
                            {pendingCount}
                          </span>
                        ) : null;
                      })()}
                    </h3>
                    <button
                      type="button"
                      onClick={copyApplyLink}
                      className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 text-white text-[12px] font-semibold px-3 py-1.5 rounded-md"
                      data-testid="copy-apply-link"
                      title={`${window.location.origin}/apply-new-offer/${selectedPublisher.id}`}
                    >
                      <LinkIcon className="w-3.5 h-3.5" />
                      {applyLinkCopied ? 'Copied ✓' : 'Copy Apply Link'}
                    </button>
                  </div>

                  {offerRequestsLoading ? (
                    <div className="text-slate-300 text-[12px] flex items-center gap-1.5">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading offer requests…
                    </div>
                  ) : offerRequests.length === 0 ? (
                    <div className="text-slate-300 text-[12px] italic bg-slate-800/40 border border-slate-700 rounded-md p-3">
                      No offer requests submitted yet. Share the Apply Link so this publisher can pick a Home Services offer.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {offerRequests.map((r) => {
                        const isPending = r.status === 'pending';
                        const isApproved = r.status === 'approved';
                        const isRejected = r.status === 'rejected';
                        return (
                          <div
                            key={r.id}
                            className={`rounded-lg border p-3 text-[12px] ${
                              isApproved
                                ? 'border-emerald-500/40 bg-emerald-500/10'
                                : isRejected
                                ? 'border-red-500/40 bg-red-500/10'
                                : 'border-amber-500/40 bg-amber-500/10'
                            }`}
                            data-testid={`offer-request-${r.id}`}
                          >
                            <div className="flex items-start justify-between gap-3 mb-2">
                              <div className="min-w-0">
                                <div className="text-white font-semibold text-[13px] truncate">
                                  {r.offer_name}
                                </div>
                                <div className="text-slate-300 flex items-center gap-1.5 mt-0.5">
                                  <span className="inline-flex bg-slate-900/60 border border-slate-600 text-slate-100 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest">
                                    {r.traffic_source_label}
                                  </span>
                                  <span className="text-slate-400 text-[11px]">
                                    · {new Date(r.created_at).toLocaleString()}
                                  </span>
                                </div>
                              </div>
                              <span
                                className={`shrink-0 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border ${
                                  isApproved
                                    ? 'bg-emerald-500/20 text-emerald-200 border-emerald-400/40'
                                    : isRejected
                                    ? 'bg-red-500/20 text-red-200 border-red-400/40'
                                    : 'bg-amber-500/20 text-amber-100 border-amber-400/40'
                                }`}
                                data-testid={`offer-request-status-${r.id}`}
                              >
                                {r.status}
                              </span>
                            </div>

                            {r.source_fields && Object.keys(r.source_fields).length > 0 && (
                              <div className="grid grid-cols-1 gap-y-1.5 mt-2 mb-2 text-[11px]" data-testid={`offer-request-fields-${r.id}`}>
                                {Object.entries(r.source_fields).map(([k, v]) => {
                                  const value = v == null ? '' : String(v);
                                  const isUrl = /^https?:\/\//i.test(value.trim());
                                  return (
                                    <div key={k} className="flex flex-col sm:flex-row sm:items-start sm:gap-2 min-w-0">
                                      <span className="text-slate-300 font-semibold uppercase tracking-wide text-[10px] sm:min-w-[130px] sm:pt-0.5 shrink-0">
                                        {k.replace(/_/g, ' ')}:
                                      </span>
                                      <div className="flex items-start gap-1.5 min-w-0 flex-1">
                                        {isUrl ? (
                                          <a
                                            href={value}
                                            target="_blank"
                                            rel="noreferrer noopener"
                                            className="text-white hover:text-blue-200 underline decoration-slate-500 hover:decoration-blue-300 font-mono break-all"
                                            data-testid={`field-link-${r.id}-${k}`}
                                          >
                                            {value}
                                          </a>
                                        ) : (
                                          <span className="text-white font-mono break-all">
                                            {value || '—'}
                                          </span>
                                        )}
                                        {value && (
                                          <button
                                            type="button"
                                            onClick={() => {
                                              navigator.clipboard?.writeText(value);
                                            }}
                                            title={`Copy ${k.replace(/_/g, ' ')}`}
                                            className="shrink-0 mt-0.5 text-slate-400 hover:text-blue-300"
                                            data-testid={`field-copy-${r.id}-${k}`}
                                          >
                                            <Copy className="w-3 h-3" />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {(r.proofs && r.proofs.length > 0) ? (
                              <div className="mt-1 flex flex-wrap gap-1.5" data-testid={`offer-request-proofs-${r.id}`}>
                                {r.proofs.map((p, i) => (
                                  <a
                                    key={i}
                                    href={p.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-1 bg-slate-900/50 border border-blue-400/50 hover:border-blue-300 text-blue-200 rounded-md px-2 py-1 text-[11px] font-medium"
                                    title={p.filename || `Proof ${i + 1}`}
                                  >
                                    <FileText className="w-3 h-3" /> Proof {i + 1}
                                  </a>
                                ))}
                              </div>
                            ) : r.proof_screenshot_url ? (
                              <a
                                href={r.proof_screenshot_url}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1.5 text-blue-300 hover:text-blue-200 underline text-[11px] font-medium mt-1"
                                data-testid={`offer-request-proof-${r.id}`}
                              >
                                <FileText className="w-3.5 h-3.5" /> View proof screenshot
                              </a>
                            ) : null}
                            {r.admin_note && (
                              <div className="mt-1 text-[11px] text-slate-300 italic">
                                Admin note: {r.admin_note}
                              </div>
                            )}

                            {isPending && (
                              <div className="flex items-center gap-2 mt-2 pt-2 border-t border-amber-200/60">
                                <button
                                  type="button"
                                  disabled={reviewingReqId === r.id}
                                  onClick={() => approveOfferRequest(r.id)}
                                  className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-[11px] font-semibold px-2.5 py-1 rounded-md"
                                  data-testid={`approve-offer-${r.id}`}
                                >
                                  <CheckCircle className="w-3 h-3" /> Approve Offer
                                </button>
                                <button
                                  type="button"
                                  disabled={reviewingReqId === r.id}
                                  onClick={() => rejectOfferRequest(r.id)}
                                  className="inline-flex items-center gap-1 bg-white hover:bg-red-50 border border-red-300 text-red-700 text-[11px] font-semibold px-2.5 py-1 rounded-md"
                                  data-testid={`reject-offer-${r.id}`}
                                >
                                  Reject
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Live Offers snapshot (approved requests joined onto publisher record) */}
                  {selectedPublisher.live_offers && selectedPublisher.live_offers.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-700/60">
                      <div className="text-[11px] uppercase tracking-widest text-slate-300 font-semibold mb-2">
                        Live Offers · {selectedPublisher.live_offers.length}
                      </div>
                      <div className="flex flex-wrap gap-1.5" data-testid="live-offers-list">
                        {selectedPublisher.live_offers.map((lo, i) => (
                          <span
                            key={i}
                            className="inline-flex items-center gap-1 bg-emerald-500/15 text-emerald-200 border border-emerald-400/40 text-[11px] font-medium px-2 py-0.5 rounded-full"
                          >
                            <CheckCircle className="w-3 h-3" /> {lo.offer_name}
                            <span className="text-emerald-300/70 text-[10px]">· {lo.traffic_source_label}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Decision Log — durable audit trail of every rejection with
                      the picked reason so admins can trace history later. */}
                  {selectedPublisher.decision_log && selectedPublisher.decision_log.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-700/60" data-testid="decision-log">
                      <div className="text-[11px] uppercase tracking-widest text-slate-300 font-semibold mb-2 flex items-center gap-1.5">
                        <ClipboardCheck className="w-3.5 h-3.5 text-slate-300" /> Decision Log · {selectedPublisher.decision_log.length}
                      </div>
                      <div className="space-y-1.5">
                        {[...selectedPublisher.decision_log].reverse().map((d, i) => (
                          <div
                            key={i}
                            className="bg-slate-800/60 border border-slate-700 rounded-md px-3 py-2 text-[11px] text-slate-200"
                            data-testid={`decision-log-entry-${i}`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="uppercase tracking-widest font-semibold text-[10px] text-red-300">
                                {(d.action || 'decision').replace(/_/g, ' ')}
                              </span>
                              <span className="text-slate-400 font-mono text-[10px]">
                                {d.at ? new Date(d.at).toLocaleString() : ''}
                              </span>
                            </div>
                            {d.offer_name && (
                              <div className="mt-0.5 text-slate-300">
                                Offer: <span className="font-medium text-white">{d.offer_name}</span>
                              </div>
                            )}
                            <div className="mt-0.5">
                              <span className="text-slate-400">Reason:</span>{' '}
                              <span className="font-medium text-white">{d.reason_label || '—'}</span>
                            </div>
                            {d.admin_email && (
                              <div className="text-slate-400 mt-0.5">by <span className="font-mono">{d.admin_email}</span></div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Admin Notes — internal annotations on the publisher record */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-slate-900 text-[13px] uppercase tracking-[0.08em] flex items-center gap-2">
                      <StickyNote className="w-4 h-4 text-amber-600" strokeWidth={2.25} />
                      Admin Notes
                    </h3>
                    {selectedPublisher.admin_notes_updated_at && (
                      <span className="text-[11px] text-slate-500" data-testid="notes-updated-at">
                        Updated {new Date(selectedPublisher.admin_notes_updated_at).toLocaleString()}
                      </span>
                    )}
                  </div>
                  <textarea
                    value={notesDraft}
                    onChange={(e) => setNotesDraft(e.target.value)}
                    rows={4}
                    maxLength={10000}
                    placeholder="Internal notes on this publisher (visible to admins only)…"
                    className="w-full px-3 py-2.5 rounded-md bg-white border border-gray-300 text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 transition resize-y"
                    data-testid="admin-notes-textarea"
                  />
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[11px] text-slate-500">
                      {notesDraft.length}/10000 characters · admin-only
                    </span>
                    <div className="flex items-center gap-2">
                      {notesSavedAt && (
                        <span className="text-[11px] text-emerald-700 font-semibold flex items-center gap-1" data-testid="notes-saved-indicator">
                          <CheckCircle className="w-3.5 h-3.5" />
                          Saved
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={handleSaveNotes}
                        disabled={notesSaving || (notesDraft === (selectedPublisher.admin_notes || ''))}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold shadow-sm hover:shadow disabled:opacity-50 disabled:cursor-not-allowed transition"
                        data-testid="save-notes-btn"
                      >
                        {notesSaving ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Save className="w-3.5 h-3.5" strokeWidth={2.25} />
                        )}
                        {notesSaving ? 'Saving…' : 'Save notes'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* PDF Downloads */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="font-semibold mb-3 text-slate-900 text-[13px] uppercase tracking-[0.08em]">Master Publisher Agreement</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* Admin variant */}
                    <div className="rounded-lg border border-gray-200 bg-slate-50/60 p-3">
                      <div className="flex items-center gap-2 text-slate-800 mb-2 text-[13px] font-semibold">
                        <FileText className="w-4 h-4 text-slate-700" strokeWidth={2.25} />
                        Admin PDF <span className="text-slate-500 font-normal text-[11px]">(with risk report)</span>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleViewPdf(selectedPublisher.id, 'admin', selectedPublisher.full_name)}
                          className="flex-1 bg-slate-900 hover:bg-slate-800 text-white py-2 rounded-md font-medium text-[13px] flex items-center justify-center gap-1.5 transition"
                          data-testid="view-admin-pdf-btn"
                        >
                          <Eye className="w-3.5 h-3.5" strokeWidth={2.5} />
                          View
                        </button>
                        <button
                          onClick={() => handleDownloadPdf(selectedPublisher.id, 'admin')}
                          className="flex-1 bg-white hover:bg-gray-50 text-slate-900 py-2 rounded-md font-medium text-[13px] flex items-center justify-center gap-1.5 border border-gray-300 transition"
                          data-testid="download-admin-pdf-btn"
                        >
                          <Download className="w-3.5 h-3.5" strokeWidth={2.5} />
                          Download
                        </button>
                      </div>
                    </div>
                    {/* Publisher variant */}
                    <div className="rounded-lg border border-gray-200 bg-slate-50/60 p-3">
                      <div className="flex items-center gap-2 text-slate-800 mb-2 text-[13px] font-semibold">
                        <FileText className="w-4 h-4 text-slate-700" strokeWidth={2.25} />
                        Publisher PDF <span className="text-slate-500 font-normal text-[11px]">(safe to share)</span>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleViewPdf(selectedPublisher.id, 'publisher', selectedPublisher.full_name)}
                          className="flex-1 bg-slate-900 hover:bg-slate-800 text-white py-2 rounded-md font-medium text-[13px] flex items-center justify-center gap-1.5 transition"
                          data-testid="view-publisher-pdf-btn"
                        >
                          <Eye className="w-3.5 h-3.5" strokeWidth={2.5} />
                          View
                        </button>
                        <button
                          onClick={() => handleDownloadPdf(selectedPublisher.id, 'publisher')}
                          className="flex-1 bg-white hover:bg-gray-50 text-slate-900 py-2 rounded-md font-medium text-[13px] flex items-center justify-center gap-1.5 border border-gray-300 transition"
                          data-testid="download-publisher-pdf-btn"
                        >
                          <Download className="w-3.5 h-3.5" strokeWidth={2.5} />
                          Download
                        </button>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    View opens the PDF right here in a popup — nothing saves to your computer.
                  </p>
                </div>

                {/* Primary Actions — pinned to the bottom of the modal viewport so
                    Approve / Hold / Disapprove / Blacklist are always one tap away
                    regardless of how far the admin has scrolled the details body. */}
                <div
                  className="sticky bottom-0 -mx-4 sm:-mx-5 mt-6 px-4 sm:px-5 py-3 bg-slate-950/95 backdrop-blur border-t border-slate-800 z-10 flex flex-wrap gap-2"
                  data-testid="modal-actions-bar"
                >
                  {selectedPublisher.status !== 'approved' && selectedPublisher.status !== 'terminated' && selectedPublisher.status !== 'account_closed' && (
                    <button
                      onClick={() => { handleApprove(selectedPublisher.id); setSelectedPublisher(null); }}
                      className="flex-1 min-w-[160px] bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 rounded-md font-medium text-sm flex items-center justify-center gap-2 shadow-sm hover:shadow transition"
                      data-testid="modal-approve-btn"
                    >
                      <Check className="w-4 h-4" strokeWidth={2.25} />
                      Approve Publisher
                    </button>
                  )}
                  {selectedPublisher.status !== 'on_hold' && selectedPublisher.status !== 'terminated' && selectedPublisher.status !== 'account_closed' && (
                    <button
                      onClick={() => { handleHold(selectedPublisher.id); setSelectedPublisher(null); }}
                      className="flex-1 min-w-[160px] bg-orange-600 hover:bg-orange-700 text-white py-2.5 rounded-md font-medium text-sm flex items-center justify-center gap-2 shadow-sm hover:shadow transition"
                      data-testid="modal-hold-btn"
                      title="Put on hold — publisher submitted wrong or incomplete info"
                    >
                      <PauseCircle className="w-4 h-4" strokeWidth={2.25} />
                      Hold for Review
                    </button>
                  )}
                  {selectedPublisher.status !== 'blocked' && selectedPublisher.status !== 'terminated' && selectedPublisher.status !== 'account_closed' && (
                    <button
                      onClick={() => { handleBlock(selectedPublisher.id); setSelectedPublisher(null); }}
                      className="flex-1 min-w-[160px] bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-md font-medium text-sm flex items-center justify-center gap-2 shadow-sm hover:shadow transition"
                      data-testid="modal-block-btn"
                    >
                      <Ban className="w-4 h-4" strokeWidth={2.25} />
                      Disapprove Publisher
                    </button>
                  )}
                  {/* Add to Blacklist — explicit, one-click. Uses the last admin
                      note as the reason. Available on any status so admins can
                      also blacklist previously disapproved/terminated rows.
                      Persistent in-modal feedback via blacklistState. */}
                  <button
                    onClick={handleAddToBlacklist}
                    disabled={blacklistState.status === 'saving' || blacklistState.status === 'saved'}
                    className={`flex-1 min-w-[160px] py-2.5 rounded-md font-medium text-sm flex items-center justify-center gap-2 shadow-sm hover:shadow transition text-white ${
                      blacklistState.status === 'saved'
                        ? 'bg-emerald-600 border border-emerald-500/50 cursor-default'
                        : 'bg-gradient-to-r from-rose-600 to-red-700 hover:from-rose-700 hover:to-red-800 border border-red-500/40'
                    } disabled:opacity-100`}
                    data-testid="modal-blacklist-btn"
                    title="Adds this publisher to the blacklist using the last admin note as the reason"
                  >
                    {blacklistState.status === 'saving' && (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.25} />
                        Adding to blacklist…
                      </>
                    )}
                    {blacklistState.status === 'saved' && (
                      <>
                        <CheckCircle className="w-4 h-4" strokeWidth={2.5} />
                        Added to Blacklist
                      </>
                    )}
                    {(blacklistState.status === 'idle' || blacklistState.status === 'error') && (
                      <>
                        <ShieldAlert className="w-4 h-4" strokeWidth={2.25} />
                        Add to Blacklist
                      </>
                    )}
                  </button>
                </div>

                {/* Persistent confirmation / error banner. Stays visible until
                    the admin closes the modal or opens a different publisher. */}
                {blacklistState.status === 'saved' && (
                  <div
                    className="mt-3 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 flex items-start gap-3"
                    data-testid="blacklist-success-banner"
                  >
                    <div className="w-8 h-8 rounded-md bg-emerald-500/20 flex items-center justify-center flex-shrink-0 ring-1 ring-emerald-500/40">
                      <CheckCircle className="w-4 h-4 text-emerald-300" strokeWidth={2.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-emerald-200 font-semibold text-sm">Added to blacklist</p>
                      <p className="text-emerald-300/80 text-xs mt-0.5">
                        {selectedPublisher.full_name || 'This publisher'} is now on the blacklist ledger.
                      </p>
                      {blacklistState.reason && (
                        <p className="text-emerald-200/70 text-xs mt-1.5">
                          <span className="font-medium">Reason saved:</span> <span className="italic">&ldquo;{blacklistState.reason.length > 200 ? blacklistState.reason.slice(0, 200) + '…' : blacklistState.reason}&rdquo;</span>
                        </p>
                      )}
                      <a
                        href="/admin/blacklist"
                        className="inline-flex items-center gap-1 mt-2 text-[11px] font-semibold text-emerald-200 hover:text-white underline underline-offset-2"
                        data-testid="view-blacklist-link"
                      >
                        View on blacklist page →
                      </a>
                    </div>
                  </div>
                )}
                {blacklistState.status === 'error' && (
                  <div
                    className="mt-3 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 flex items-start gap-3"
                    data-testid="blacklist-error-banner"
                  >
                    <div className="w-8 h-8 rounded-md bg-red-500/20 flex items-center justify-center flex-shrink-0 ring-1 ring-red-500/40">
                      <AlertTriangle className="w-4 h-4 text-red-300" strokeWidth={2.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-red-200 font-semibold text-sm">Could not add to blacklist</p>
                      <p className="text-red-300/80 text-xs mt-0.5">{blacklistState.reason || 'Please try again.'}</p>
                    </div>
                  </div>
                )}

                {/* Danger Zone — two distinct offboarding flows */}
                {selectedPublisher.status !== 'terminated' && selectedPublisher.status !== 'account_closed' && (
                  <div className="mt-6 pt-6 border-t-2 border-slate-200">
                    <h3 className="font-semibold text-slate-900 text-[13px] uppercase tracking-[0.08em] flex items-center gap-2 mb-3">
                      <AlertTriangle className="w-4 h-4 text-amber-600" strokeWidth={2.25} />
                      Danger Zone — Offboarding
                    </h3>
                    <div className="grid md:grid-cols-2 gap-3">
                      {/* Voluntary close */}
                      <div className="rounded-lg border border-slate-300 bg-slate-50 p-4 flex flex-col">
                        <div className="flex items-center gap-2 mb-2">
                          <XCircle className="w-4 h-4 text-slate-600" strokeWidth={2.25} />
                          <span className="text-[12px] font-bold uppercase tracking-wider text-slate-700">Close Account</span>
                        </div>
                        <p className="text-xs text-slate-600 leading-relaxed flex-1">
                          For <strong>voluntary closures</strong> — publisher leaves on good terms or
                          mutually parts ways. Sends a polite &ldquo;Account closed&rdquo; email. ID is scrubbed
                          from Cloudinary; audit trail is preserved.
                        </p>
                        <button
                          type="button"
                          onClick={handleCloseAccount}
                          disabled={closing}
                          className="mt-3 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-md bg-slate-700 hover:bg-slate-800 text-white text-xs font-semibold shadow-sm hover:shadow disabled:opacity-50 disabled:cursor-not-allowed transition"
                          data-testid="modal-close-account-btn"
                        >
                          {closing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" strokeWidth={2.25} />}
                          {closing ? 'Closing…' : 'Close Account'}
                        </button>
                      </div>
                      {/* Punitive terminate */}
                      <div className="rounded-lg border-2 border-red-300 bg-red-50 p-4 flex flex-col">
                        <div className="flex items-center gap-2 mb-2">
                          <Gavel className="w-4 h-4 text-red-700" strokeWidth={2.25} />
                          <span className="text-[12px] font-bold uppercase tracking-wider text-red-800">Terminate</span>
                        </div>
                        <p className="text-xs text-red-700 leading-relaxed flex-1">
                          For <strong>breach of agreement</strong> — fake calls, TCPA violations, fraud.
                          Sends formal &ldquo;Notice of termination&rdquo;. Pending earnings are
                          <strong> forfeited</strong> per Section 14. <strong>Cannot be undone.</strong>
                        </p>
                        <button
                          type="button"
                          onClick={handleTerminate}
                          disabled={terminating}
                          className="mt-3 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white text-xs font-semibold shadow-sm hover:shadow disabled:opacity-50 disabled:cursor-not-allowed transition"
                          data-testid="modal-terminate-btn"
                        >
                          {terminating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Gavel className="w-3.5 h-3.5" strokeWidth={2.25} />}
                          {terminating ? 'Terminating…' : 'Terminate Account'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {selectedPublisher.status === 'account_closed' && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="rounded-lg border border-slate-300 bg-slate-100 p-4 text-center" data-testid="account-closed-banner">
                      <p className="text-sm font-semibold text-slate-700 inline-flex items-center gap-2">
                        <XCircle className="w-4 h-4" />
                        Account closed
                        {selectedPublisher.closed_at && (
                          <span className="text-xs text-slate-500 font-normal">
                            on {new Date(selectedPublisher.closed_at).toLocaleString()}
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        Voluntary closure. ID artefacts scrubbed; audit trail preserved.
                      </p>
                    </div>
                  </div>
                )}

                {selectedPublisher.status === 'terminated' && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="rounded-lg border-2 border-red-300 bg-red-50 p-4 text-center" data-testid="terminated-banner">
                      <p className="text-sm font-semibold text-red-800 inline-flex items-center gap-2">
                        <Gavel className="w-4 h-4" />
                        Account terminated for breach of agreement
                        {selectedPublisher.terminated_at && (
                          <span className="text-xs text-red-600 font-normal">
                            on {new Date(selectedPublisher.terminated_at).toLocaleString()}
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-red-700 mt-1">
                        Pending earnings forfeited. ID artefacts scrubbed; audit trail preserved.
                      </p>
                    </div>
                  </div>
                )}

                {/* Hard Delete — wipes the record permanently. Always available. */}
                <div className="mt-4 rounded-lg border-2 border-red-900/40 bg-red-50 p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-red-900 text-[13px] uppercase tracking-[0.08em] flex items-center gap-2">
                        <Trash2 className="w-4 h-4" strokeWidth={2.25} />
                        Hard Delete (Permanent)
                      </h3>
                      <p className="text-xs text-red-800 mt-2 leading-relaxed">
                        Wipe this record from MongoDB entirely. <strong>No email is sent. No audit
                        trail is kept.</strong> The Government ID is purged from Cloudinary. Use
                        only for spam, accidental duplicates, or obvious test rows.{' '}
                        <strong>This cannot be undone.</strong>
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleHardDelete}
                      disabled={bulkRunning}
                      className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-red-900 hover:bg-red-950 text-white text-xs font-semibold shadow-sm hover:shadow disabled:opacity-50 disabled:cursor-not-allowed transition border border-red-700"
                      data-testid="modal-hard-delete-btn"
                    >
                      {bulkRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" strokeWidth={2.25} />}
                      Delete Permanently
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Publisher Modal — separate lightweight modal for correcting typos */}
        {editingPublisher && (
          <div
            className="fixed inset-0 bg-slate-900/50 backdrop-blur-[2px] flex items-center justify-center p-4 z-[60]"
            data-testid="edit-publisher-modal"
          >
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-200 shadow-xl">
              <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                    <Edit3 className="w-5 h-5 text-blue-600" />
                    Edit publisher details
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Fix typos, update contact info. ID image &amp; status are not editable here.
                  </p>
                </div>
                <button
                  onClick={() => setEditingPublisher(null)}
                  className="p-2 rounded-md hover:bg-slate-100 text-slate-600"
                  data-testid="edit-modal-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="grid sm:grid-cols-2 gap-3">
                  <EditField label="Full name" value={editDraft.full_name} onChange={(v) => setEditDraft({...editDraft, full_name: v})} testid="edit-full-name" />
                  <EditField label="Email" type="email" value={editDraft.email} onChange={(v) => setEditDraft({...editDraft, email: v})} testid="edit-email" />
                  <EditField label="Company name" value={editDraft.company_name} onChange={(v) => setEditDraft({...editDraft, company_name: v})} testid="edit-company" />
                  <EditField label="Contact method" value={editDraft.contact_method} onChange={(v) => setEditDraft({...editDraft, contact_method: v})} testid="edit-contact-method" />
                  <EditField label="Messaging platform" value={editDraft.messaging_platform} onChange={(v) => setEditDraft({...editDraft, messaging_platform: v})} testid="edit-messaging-platform" />
                  <EditField label="Messaging handle" value={editDraft.messaging_handle} onChange={(v) => setEditDraft({...editDraft, messaging_handle: v})} testid="edit-messaging-handle" />
                  <EditField label="Location" value={editDraft.publisher_location} onChange={(v) => setEditDraft({...editDraft, publisher_location: v})} testid="edit-location" />
                  <EditField label="Country" value={editDraft.country} onChange={(v) => setEditDraft({...editDraft, country: v})} testid="edit-country" />
                  <EditField label="Experience duration" value={editDraft.experience_duration} onChange={(v) => setEditDraft({...editDraft, experience_duration: v})} testid="edit-experience" />
                  <EditField label="Previous companies" value={editDraft.previous_companies} onChange={(v) => setEditDraft({...editDraft, previous_companies: v})} testid="edit-previous-companies" />
                  <EditField label="Call tracking platform" value={editDraft.call_tracking_platform} onChange={(v) => setEditDraft({...editDraft, call_tracking_platform: v})} testid="edit-call-tracking" />
                  <EditField label="Call type" value={editDraft.call_type} onChange={(v) => setEditDraft({...editDraft, call_type: v})} testid="edit-call-type" />
                  <EditField label="Daily call volume" value={editDraft.daily_call_volume} onChange={(v) => setEditDraft({...editDraft, daily_call_volume: v})} testid="edit-call-volume" />
                </div>
              </div>

              <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-3 flex items-center justify-end gap-2">
                <button
                  onClick={() => setEditingPublisher(null)}
                  className="px-4 h-9 rounded-md text-sm font-medium border border-slate-200 text-slate-700 hover:bg-slate-50"
                  data-testid="edit-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={saveEdit}
                  disabled={editSaving}
                  className="flex items-center gap-1.5 px-5 h-9 rounded-md text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60"
                  data-testid="edit-save"
                >
                  {editSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save changes
                </button>
              </div>
            </div>
          </div>
        )}

        {/* PDF Viewer Modal — inline agreement preview, no download */}
        {pdfPreview && (
          <div
            className="fixed inset-0 bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6 z-[70]"
            data-testid="pdf-preview-modal"
            onClick={(e) => { if (e.target === e.currentTarget) closePdfPreview(); }}
          >
            <div className="bg-white rounded-lg w-full h-full max-w-[1400px] max-h-[95vh] flex flex-col shadow-2xl overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-gradient-to-r from-slate-900 to-slate-800 text-white">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-slate-300 font-semibold">
                    <FileText className="w-3.5 h-3.5" strokeWidth={2.5} />
                    Master Publisher Agreement
                    <span className={`ml-1 px-1.5 py-0.5 rounded text-[9px] font-bold ${
                      pdfPreview.type === 'admin'
                        ? 'bg-amber-500/20 text-amber-200 border border-amber-500/40'
                        : 'bg-emerald-500/20 text-emerald-200 border border-emerald-500/40'
                    }`}>
                      {pdfPreview.type === 'admin' ? 'ADMIN · WITH RISK REPORT' : 'PUBLISHER · SHAREABLE'}
                    </span>
                  </div>
                  <div className="mt-1 text-white font-semibold text-sm truncate">
                    {pdfPreview.publisherName || pdfPreview.appId}
                  </div>
                </div>
                <button
                  onClick={closePdfPreview}
                  className="flex items-center gap-1.5 px-3 h-9 rounded-md text-[13px] font-semibold bg-white/10 hover:bg-white/20 border border-white/20 text-white transition"
                  data-testid="pdf-preview-close-btn"
                >
                  <X className="w-4 h-4" strokeWidth={2.5} />
                  Close
                </button>
              </div>

              {/* Body */}
              <div className="flex-1 bg-slate-100 relative overflow-hidden">
                {pdfPreview.loading && (
                  <div className="absolute inset-0 flex items-center justify-center text-slate-600">
                    <Loader2 className="w-6 h-6 animate-spin mr-2 text-slate-500" strokeWidth={2.25} />
                    <span className="text-sm font-medium">Loading agreement…</span>
                  </div>
                )}
                {pdfPreview.error && (
                  <div className="absolute inset-0 flex items-center justify-center flex-col gap-2 text-red-700">
                    <AlertTriangle className="w-8 h-8" />
                    <div className="font-semibold">Couldn&rsquo;t load the PDF.</div>
                    <div className="text-xs text-slate-600">{pdfPreview.error}</div>
                  </div>
                )}
                {pdfPreview.blobUrl && (
                  <>
                    <iframe
                      key={pdfPreview.blobUrl}
                      src={`${pdfPreview.blobUrl}#toolbar=1&navpanes=0&view=FitH`}
                      title="Master Publisher Agreement PDF"
                      className="w-full h-full border-0 bg-white"
                      data-testid="pdf-preview-iframe"
                    />
                    {/* Fallback link for the tiny minority of browsers that
                        can't render PDFs inline (very old browsers, some
                        embedded webviews). Renders behind the iframe so
                        successful inline rendering hides it entirely. */}
                    <noscript>
                      <a
                        href={pdfPreview.blobUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute inset-0 flex items-center justify-center text-blue-600 underline"
                      >
                        Open PDF in a new tab
                      </a>
                    </noscript>
                  </>
                )}
              </div>

              {/* Footer hint */}
              <div className="px-5 py-2 border-t border-slate-200 bg-slate-50 text-[11px] text-slate-500 flex items-center justify-between">
                <span>Press <kbd className="px-1.5 py-0.5 bg-white border border-slate-300 rounded text-slate-700 font-mono text-[10px]">Esc</kbd> or click outside to close · nothing is saved to your computer</span>
              </div>
            </div>
          </div>
        )}

        {/* Structured rejection modal — shared by publisher disapprove
            and offer-request reject flows. */}
        <RejectReasonModal
          open={!!rejectModal}
          title={rejectModal?.title}
          subtitle={rejectModal?.subtitle}
          targetName={rejectModal?.targetName}
          confirmLabel={rejectModal?.confirmLabel}
          onCancel={() => setRejectModal(null)}
          onConfirm={handleRejectConfirm}
        />
    </AdminShell>
  );
};

// Small reusable input row for the edit modal
const EditField = ({ label, value, onChange, type = 'text', testid }) => (
  <label className="block">
    <span className="block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-[0.14em]">{label}</span>
    <input
      type={type}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full h-9 text-sm border border-slate-200 rounded-md px-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none bg-white"
    />
  </label>
);

export default AdminPublishersPage;
